/**
 * A function to create a log object for the 'appMarketHoverApp' event (evid: 1852)
 *
 * Event description: When the user hovers over an app
 *
 * @param {appMarketHoverAppParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {string} app_subcategory - if app was shown in a category page where sub category was selected - name the sub category.
 * @property {string} badge_name - name of badge of app box (e.g - Premium Benefit)
 * @property {GUID} esi - -
 * @property {GUID} impression_id - impression id - unique for which impression
 * @property {string} impression_type - type the of app (app box, search suggestion etc)
 * @property {number} index - The listing ranking of the number in the presented page.
 * @property {string} market - market type - editor, dashboard, standalone
 * @property {GUID} msid - msid
 * @property {string} search_term - search_term used to expose this app
 * @property {string} section - section in which app box is located
 * @property {number} section_index - from top = 0, to last = 0+n. what is the ranking order of the section presented?
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} tag_name - the page type name: for category - the category name, for app page - the app name, etc.
 * @property {string} tag_type - the page type
 * @property {string} vertical
 * @return {LogObject<appMarketHoverAppParams>} LogObject<appMarketHoverAppParams>
 */
export function appMarketHoverApp(params) {
    return { evid: 1852, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__17' event (evid: 17)
 *
 * Event description: welcome screen -> welcome screen loaded
 *
 * @param {__deprecated__17Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__17Params>} LogObject<__deprecated__17Params>
 */
export function __deprecated__17(params) {
    return { evid: 17, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppInstallationWithSilentConsent' event (evid: 148)
 *
 * Event description: App Installation started with silent consent - for wix apps or apps with silent consent.
 *
 * @param {appMarketAppInstallationWithSilentConsentParams} params - The parameters for this event
 * @property {string} app_id - if app page opened, app_id. Else = null
 * @property {string} app_subcategory
 * @property {GUID} collimp_id - collection impression guid
 * @property {GUID} esi - esi
 * @property {GUID} impression_id - Impression id - unique for which impression
 * @property {boolean} in_context_view - bool
 * @property {string} market - market type - editor, dashboard, standalone
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} navigation_source
 * @property {string} referral_info - referral_info (correct, no typo)
 * @property {number} referral_section_index - what is the index number of the app box that was clicked in previous page
 * @property {string} referral_section_name
 * @property {string} referral_tag_name - what is the tag_name that referred to this page
 * @property {string} search_term
 * @property {string} section - In which section is that app located?
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} slug_index_list - list of slug_id presented in homepage from top to bottom
 * @property {string} tag_name - the name of the tag type (if collection - must have apps, free apps etc. if category - music, video , business etc)
 * @property {string} tag_type
 * @property {string} vertical
 * @property {string} widget_referral - is flow referred from widget
 * @return {LogObject<appMarketAppInstallationWithSilentConsentParams>} LogObject<appMarketAppInstallationWithSilentConsentParams>
 */
export function appMarketAppInstallationWithSilentConsent(params) {
    return { evid: 148, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketUpgradeWixSiteClick' event (evid: 70)
 *
 * Event description: When trying to install Apps that require premium wix site, prompt will be opened, clicking on the button "Upgrade Site" will send this BI event.
 *
 * @param {appMarketUpgradeWixSiteClickParams} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - determines which market initiated this event (editor or dashboard)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketUpgradeWixSiteClickParams>} LogObject<appMarketUpgradeWixSiteClickParams>
 */
export function appMarketUpgradeWixSiteClick(params) {
    return { evid: 70, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__60' event (evid: 60)
 *
 * Event description: App Market - Close Market Button [x]
 *
 * @param {__deprecated__60Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} open_origin - {add_panel...}
 * @property {number} seconds_opened - how long was the session in the app market (open to close in seconds)
 * @property {string} vertical
 * @return {LogObject<__deprecated__60Params>} LogObject<__deprecated__60Params>
 */
export function __deprecated__60(params) {
    return { evid: 60, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'addAppClick' event (evid: 31)
 *
 * Event description: add app or claim app click in app page
 *
 * @param {addAppClickParams} params - The parameters for this event
 * @property {string} all_requirements_filled - yes/no/no-requirements
 * @property {GUID} app_id - app_id
 * @property {string} category - Every app belongs to one category. which one is the app category of this app?
 * @property {string} click_location - 'main_cta' or 'sticky_header'
 * @property {GUID} collimp_id - collection impression guid
 * @property {string} cta_type - the cta is always 'add to site', but the flow type could be 'add app' or 'claim app' for bundles
 * @property {GUID} esi
 * @property {boolean} has_app_requirement - true\false
 * @property {GUID} impression_id - impression id - unique for which impression
 * @property {boolean} in_context_view - boolean, is true if the click made in context view.
 * @property {number} index - The listing ranking of the number in the presented page.
 * @property {boolean} is_app_dependency - true\false
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - {editor,dashboard,standalone, da_widget}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} navigation_source - e.g. top-search,home, menu, standalone-header, search_result_page, search_result_empty_page
 * @property {number} number_of_depended_apps - number_of_depended_apps
 * @property {string} pageType - Type of page used when add app was clicked ( Homepage, App page, Developer page , collection page etc)
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_section_name - section that referred this, for example: search-result	, category
 * @property {string} referral_sub_tag_name - e.g. sub category name
 * @property {string} referral_tag_type - previous page type
 * @property {string} require_type - the unfulfilled requirement (same as prompt message) , 'null' if all fulfilled
 * @property {string} search_term - {html, analytics,...}
 * @property {string} section - In which section is that app located?
 * @property {number} section_index - from top = 0, to last = 0+n. what is the ranking order of the section presented?
 * @property {string} section_name - name of section's title presented
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} tag_name - name of the specific tag ('must have apps' , 'music' etc)
 * @property {string} tag_type - type of page opened - category, homepage, app page etc
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @property {string} widget_name - when trying to add an app, if app the inside a widget - what is the widget name. else null
 * @property {string} widget_referral - is flow referred from widget
 * @return {LogObject<addAppClickParams>} LogObject<addAppClickParams>
 */
export function addAppClick(params) {
    return { evid: 31, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__86' event (evid: 86)
 *
 * Event description: review system -> share review
 *
 * @param {__deprecated__86Params} params - The parameters for this event
 * @property {GUID} product_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {string} type - {facebook, twitter}
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__86Params>} LogObject<__deprecated__86Params>
 */
export function __deprecated__86(params) {
    return { evid: 86, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1011' event (evid: 1011)
 *
 * Event description: Click any button on the split page
 *
 * @param {__deprecated__1011Params} params - The parameters for this event
 * @property {string} button_name - 'test_app', 'submit_proposal'
 * @property {GUID} esi - session ID
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1011Params>} LogObject<__deprecated__1011Params>
 */
export function __deprecated__1011(params) {
    return { evid: 1011, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__28' event (evid: 28)
 *
 * Event description: search autocomplete results
 *
 * @param {__deprecated__28Params} params - The parameters for this event
 * @property {string} auto_search_term - auto_search_term
 * @property {GUID} esi
 * @property {GUID} first_result_app_id - [first_result_app_id]
 * @property {string} market - {editor,dashboard,standalone}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__28Params>} LogObject<__deprecated__28Params>
 */
export function __deprecated__28(params) {
    return { evid: 28, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1100' event (evid: 1100)
 *
 * Event description: Click a button on the top banner of the new Dev Center
 *
 * @param {__deprecated__1100Params} params - The parameters for this event
 * @property {string} button_name - 'home', 'add_app', 'documentation', 'support', 'app_market', 'dev_account', 'logout'
 * @property {GUID} esi - Session ID
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1100Params>} LogObject<__deprecated__1100Params>
 */
export function __deprecated__1100(params) {
    return { evid: 1100, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1021' event (evid: 1021)
 *
 * Event description: Click on view more apps
 *
 * @param {__deprecated__1021Params} params - The parameters for this event
 * @property {GUID} esi - session ID
 * @property {GUID} msid - msid
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1021Params>} LogObject<__deprecated__1021Params>
 */
export function __deprecated__1021(params) {
    return { evid: 1021, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'searchSearchClick' event (evid: 13)
 *
 * Event description: search -> search click
 *
 * @param {searchSearchClickParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {number} index - index number of app preseting ranking order
 * @property {string} market - {editor,dashboard,standalone}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} origin - {search_auto, direct}
 * @property {string} page_name - page name
 * @property {string} search_location - search_location
 * @property {string} search_term - {html, analytics,...}
 * @property {string} search_term_typed - the actual term before autocomplete choice
 * @property {string} search_type - direct, apps, popular, categories
 * @property {string} tag_type - tag_type of searched bar
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<searchSearchClickParams>} LogObject<searchSearchClickParams>
 */
export function searchSearchClick(params) {
    return { evid: 13, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'manageAppsUpgradeSite' event (evid: 95)
 *
 * Event description: Upgrade App Click in the Manage Apps
 *
 * @param {manageAppsUpgradeSiteParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} esi
 * @property {string} market - what market ? (editor, dashboard etc)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} sale_name - nov21
 * @property {string} vertical
 * @return {LogObject<manageAppsUpgradeSiteParams>} LogObject<manageAppsUpgradeSiteParams>
 */
export function manageAppsUpgradeSite(params) {
    return { evid: 95, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'errorPageLoaded' event (evid: 200)
 *
 * Event description: error page loaded
 *
 * @param {errorPageLoadedParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} referral_info - referral_info
 * @property {GUID} site_id - site_id
 * @property {string} state - state
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<errorPageLoadedParams>} LogObject<errorPageLoadedParams>
 */
export function errorPageLoaded(params) {
    return { evid: 200, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketToastPresented' event (evid: 150)
 *
 * Event description: Toast is presented for user to take action for app.
 *
 * @param {appMarketToastPresentedParams} params - The parameters for this event
 * @property {string} action_cta - action required
 * @property {string} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - dashboard, editor etc
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketToastPresentedParams>} LogObject<appMarketToastPresentedParams>
 */
export function appMarketToastPresented(params) {
    return { evid: 150, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketWidgetsClickAction' event (evid: 3)
 *
 * Event description: When user clicks on a possible action in a widget: could be box of apps collection, category or simple articles
 *
 * @param {appMarketWidgetsClickActionParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - market platform
 * @property {string} marketVersion - version of market
 * @property {GUID} msid - msid
 * @property {string} page_name - page name
 * @property {string} section - what is the section (widget) name
 * @property {string} tag_name - what was the actual page name , where the widget was shown? (app_id of app, homepage etc)
 * @property {string} tag_name_clicked - name of tag clicked on to go
 * @property {string} tag_type - Where was the widget when user clicked on it? (homepage, app page)
 * @property {string} tag_type_clicked - type of tag clicked on to go
 * @property {string} vertical
 * @property {string} widget_type - collection/category
 * @return {LogObject<appMarketWidgetsClickActionParams>} LogObject<appMarketWidgetsClickActionParams>
 */
export function appMarketWidgetsClickAction(params) {
    return { evid: 3, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__105' event (evid: 105)
 *
 * Event description: open manage apps section
 *
 * @param {__deprecated__105Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - editor,dashboard
 * @property {string} marketVersion - what market version (old or new market)
 * @property {string} vertical
 * @return {LogObject<__deprecated__105Params>} LogObject<__deprecated__105Params>
 */
export function __deprecated__105(params) {
    return { evid: 105, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageAppGalleryClick' event (evid: 173)
 *
 * Event description: When clicking the gallery arrows (Left or Right) on the app page
 *
 * @param {appMarketAppPageAppGalleryClickParams} params - The parameters for this event
 * @property {string} action - right, left, dot (dot is the click in the middle bottom to choose specific image, for now is not being sent)
 * @property {string} app_id - app id
 * @property {GUID} esi
 * @property {string} market - editor, dashboard, standalone
 * @property {string} marketVersion - new
 * @property {GUID} msid - msid of site
 * @property {string} tag_name - name of the tag
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageAppGalleryClickParams>} LogObject<appMarketAppPageAppGalleryClickParams>
 */
export function appMarketAppPageAppGalleryClick(params) {
    return { evid: 173, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageClick' event (evid: 1859)
 *
 * Event description: User clicks on a CTA in the app page.
 *
 * @param {appMarketAppPageClickParams} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} cta - company_name, read_more, read_less
 * @property {GUID} esi
 * @property {string} location - overview
 * @property {string} market - market
 * @property {GUID} msid - msid
 * @property {Url} url - url of external link
 * @property {GUID} uuid - uuid
 * @property {string} value - the specific value of the cta
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageClickParams>} LogObject<appMarketAppPageClickParams>
 */
export function appMarketAppPageClick(params) {
    return { evid: 1859, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__59' event (evid: 59)
 *
 * Event description: Prompt open when user presses the delete button over an app , in the Manage Apps section.
 *
 * @param {__deprecated__59Params} params - The parameters for this event
 * @property {string} action - User's click in the prompt menu (delete app, cancel, go got it, learn more,[x] ....
 * @property {GUID} appId - app id
 * @property {GUID} esi
 * @property {string} market - Name of market manage apps was used from (dashboard, market)
 * @property {string} promptType - what kind of prompt was shown? (dashboard app, page app, multi widget app, hybrid app, upgraded app)
 * @property {string} vertical
 * @return {LogObject<__deprecated__59Params>} LogObject<__deprecated__59Params>
 */
export function __deprecated__59(params) {
    return { evid: 59, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketMenuNavigation' event (evid: 116)
 *
 * Event description: User navigate through the App Market, using the side menu.
 *
 * @param {appMarketMenuNavigationParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {number} index - index of label in menu
 * @property {string} market - editor, dashboard etc
 * @property {string} marketVersion - version of the market - old or new
 * @property {string} menu_item_label - name of menu label
 * @property {GUID} msid - msid
 * @property {string} slug_id - slug_id
 * @property {string} vertical
 * @return {LogObject<appMarketMenuNavigationParams>} LogObject<appMarketMenuNavigationParams>
 */
export function appMarketMenuNavigation(params) {
    return { evid: 116, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketStandaloneHomepageClicksOnGetStarted' event (evid: 138)
 *
 * Event description: user enters the standalone homepage and clicks on get started (link to Wix Studio / funnel).
 *
 * @param {appMarketStandaloneHomepageClicksOnGetStartedParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - standalone
 * @property {GUID} msid - msid
 * @property {string} page_name - homepage etc.
 * @property {string} tag_type - home page
 * @property {string} vertical
 * @return {LogObject<appMarketStandaloneHomepageClicksOnGetStartedParams>} LogObject<appMarketStandaloneHomepageClicksOnGetStartedParams>
 */
export function appMarketStandaloneHomepageClicksOnGetStarted(params) {
    return { evid: 138, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'permissionsModalTermsOfUseClick' event (evid: 44)
 *
 * Event description: Consent page - clicking on the 'terms of use' option
 *
 * @param {permissionsModalTermsOfUseClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, da_widget}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} terms_type - what was the terms and condition link type ?
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<permissionsModalTermsOfUseClickParams>} LogObject<permissionsModalTermsOfUseClickParams>
 */
export function permissionsModalTermsOfUseClick(params) {
    return { evid: 44, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__39' event (evid: 39)
 *
 * Event description: tpa message loaded
 *
 * @param {__deprecated__39Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - {dashboard}
 * @property {string} msg_type - {standard, premium, hybrid}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__39Params>} LogObject<__deprecated__39Params>
 */
export function __deprecated__39(params) {
    return { evid: 39, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketDeveloperContactBoxActionClick' event (evid: 126)
 *
 * Event description: User clicks on developer contact box action in the App page
 *
 * @param {appMarketDeveloperContactBoxActionClickParams} params - The parameters for this event
 * @property {string} actionName - name of action taken:  [visit_website, contact_support, privacy]
 * @property {string} app_id - app_id
 * @property {GUID} esi - editor session id
 * @property {string} market - dashboard, editor, standalone
 * @property {string} marketVersion - market version, "new" for new else null
 * @property {GUID} msid - msid
 * @property {string} tag_name - tag name
 * @property {string} tag_type - tag_type
 * @property {string} vertical
 * @return {LogObject<appMarketDeveloperContactBoxActionClickParams>} LogObject<appMarketDeveloperContactBoxActionClickParams>
 */
export function appMarketDeveloperContactBoxActionClick(params) {
    return { evid: 126, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'wixOfferingBoxAction' event (evid: 1849)
 *
 * Event description: Wix offering is shown/clicked on
 *
 * @param {wixOfferingBoxActionParams} params - The parameters for this event
 * @property {string} action_type - impression or click
 * @property {GUID} app_id - app_id of the wix offering
 * @property {GUID} esi
 * @property {string} market - editor/ dashboard
 * @property {GUID} msid - msid of site
 * @property {string} offering_name - wix offering name (e.g - multilingual)
 * @property {string} referral_info - search_bar, search_results_page
 * @property {string} search_term - search term used to get to the result page
 * @property {string} vertical
 * @return {LogObject<wixOfferingBoxActionParams>} LogObject<wixOfferingBoxActionParams>
 */
export function wixOfferingBoxAction(params) {
    return { evid: 1849, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__81' event (evid: 81)
 *
 * Event description: review system page view
 *
 * @param {__deprecated__81Params} params - The parameters for this event
 * @property {GUID} product_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {string} origin - {tab,link}
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__81Params>} LogObject<__deprecated__81Params>
 */
export function __deprecated__81(params) {
    return { evid: 81, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'permissionsPermissionsModalClosed' event (evid: 42)
 *
 * Event description: permissions -> permissions modal closed
 *
 * @param {permissionsPermissionsModalClosedParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {boolean} in_context_view - bool
 * @property {string} market - editor, dashboard, adi, third_party
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {string} message_type - {default, unlisted_app_warning}
 * @property {string} modalType - consent , existing
 * @property {GUID} msid - msid
 * @property {number} num_of_permissions - number of permissions requested by consent
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<permissionsPermissionsModalClosedParams>} LogObject<permissionsPermissionsModalClosedParams>
 */
export function permissionsPermissionsModalClosed(params) {
    return { evid: 42, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageOneDependencyImpression' event (evid: 1853)
 *
 * Event description: app page one dependency impression
 *
 * @param {appMarketAppPageOneDependencyImpressionParams} params - The parameters for this event
 * @property {string} app_dependency
 * @property {string} app_id - app_id
 * @property {string} dependency_type - app\ geo\ language
 * @property {GUID} esi
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - market
 * @property {string} referral_section_name - what is the section name that was clicked in previous page
 * @property {string} refferal_info - referral_info
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} type - one, multiple, pending
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageOneDependencyImpressionParams>} LogObject<appMarketAppPageOneDependencyImpressionParams>
 */
export function appMarketAppPageOneDependencyImpression(params) {
    return { evid: 1853, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketSubCategoryClick' event (evid: 118)
 *
 * Event description: User clicks on a sub-category tag, to filter in (or out) the apps that are presented in a category page. Also triggered when sub-category on sidebar is clicked.
 *
 * @param {appMarketSubCategoryClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} category_name - name of category page
 * @property {GUID} esi
 * @property {string} loaction - from where the user reached to the page, and then clicked on the sub category- side menu and etc
 * @property {string} market - dashboard, editor, standalone
 * @property {string} marketVersion - new or old
 * @property {GUID} msid - msid
 * @property {string} subcategory_name - name of sub-category clicked
 * @property {string} type - type of click: 'exclude' or 'include'
 * @property {string} vertical
 * @return {LogObject<appMarketSubCategoryClickParams>} LogObject<appMarketSubCategoryClickParams>
 */
export function appMarketSubCategoryClick(params) {
    return { evid: 118, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppInstallSuccessClientAndServer' event (evid: 10)
 *
 * Event description: New app Install event to replace the old onces (2 and 1) and fix bugs.
 *
 * @param {appMarketAppInstallSuccessClientAndServerParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} collimp_id - collection impression guid
 * @property {GUID} esi
 * @property {GUID} impression_id - Impression id - unique for which impression
 * @property {number} index - app index number when presented
 * @property {GUID} instance_id - app instance id
 * @property {boolean} is_app_dependency - true\false
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - editor, dashboard, adi, add-panel
 * @property {GUID} msid - msid
 * @property {string} navigation_source - e.g. top-search. if the user clicks on the app from a navigation source (sidebar menu/ search). from which navigation source.
 * @property {string} referral_info - referral_info (correct, no typo). The previous page tag_type
 * @property {number} referral_section_index - what is the index number of the app box that was clicked in previous page
 * @property {string} referral_section_name - what is the section name that was clicked in previous page
 * @property {string} referral_tag_name - what is the tag_name that referred to this install
 * @property {string} search_term - word used for search term, led to search result, led to app page and install
 * @property {string} slug_id - id of consistent set of apps
 * @property {number} sum_one - value 1 of ttest
 * @property {string} tag_name - what is the specific tag from which the app was installed from
 * @property {string} tag_type - what is the type of app tag? category, collection etc
 * @property {string} vertical
 * @property {string} widget_referral - is flow referred from widget
 * @return {LogObject<appMarketAppInstallSuccessClientAndServerParams>} LogObject<appMarketAppInstallSuccessClientAndServerParams>
 */
export function appMarketAppInstallSuccessClientAndServer(params) {
    return { evid: 10, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketBreadcrumbsClick' event (evid: 125)
 *
 * Event description: user clicks on breadcrumb navigation
 *
 * @param {appMarketBreadcrumbsClickParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - market
 * @property {string} marketVersion - "new" for new market, else null
 * @property {GUID} msid - msid
 * @property {number} nav_level - level of breadcrumb clicked, 1 > 2 > 3 > etc
 * @property {string} tag_name - tag name
 * @property {string} tag_name_clicked - tag name of target
 * @property {string} tag_type - tag type
 * @property {string} tag_type_clicked - target of tag type
 * @property {string} vertical
 * @return {LogObject<appMarketBreadcrumbsClickParams>} LogObject<appMarketBreadcrumbsClickParams>
 */
export function appMarketBreadcrumbsClick(params) {
    return { evid: 125, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketUpgradeSiteConnectDomainDialogBox' event (evid: 76)
 *
 * Event description: Dialog box opened when user tries to install a premium-must app.
 *
 * @param {appMarketUpgradeSiteConnectDomainDialogBoxParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {GUID} esi
 * @property {string} market - editor or dashboard
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} stepName - which step is it? 'upgrade_site' or 'connect_domain'
 * @property {string} vertical
 * @return {LogObject<appMarketUpgradeSiteConnectDomainDialogBoxParams>} LogObject<appMarketUpgradeSiteConnectDomainDialogBoxParams>
 */
export function appMarketUpgradeSiteConnectDomainDialogBox(params) {
    return { evid: 76, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__26' event (evid: 26)
 *
 * Event description: app modal -> thumbnail click
 *
 * @param {__deprecated__26Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, da_widget, premium_cart}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {string} thumbnail_type - {left, right}
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__26Params>} LogObject<__deprecated__26Params>
 */
export function __deprecated__26(params) {
    return { evid: 26, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketDeleteModalCancelAppDeleteX' event (evid: 8)
 *
 * Event description: User closes the delete app modal without deleting.
 *
 * @param {appMarketDeleteModalCancelAppDeleteXParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {string} market - market
 * @property {string} marketVersion - market version (new or null)
 * @property {GUID} msid - msid
 * @property {string} origin - click origin from? ('context_menu' etc)
 * @property {string} tag_type - Where did the delete take place from?
 * @property {string} vertical
 * @return {LogObject<appMarketDeleteModalCancelAppDeleteXParams>} LogObject<appMarketDeleteModalCancelAppDeleteXParams>
 */
export function appMarketDeleteModalCancelAppDeleteX(params) {
    return { evid: 8, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__72' event (evid: 72)
 *
 * Event description: premium cart -> app removed from cart
 *
 * @param {__deprecated__72Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - {premium_cart}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__72Params>} LogObject<__deprecated__72Params>
 */
export function __deprecated__72(params) {
    return { evid: 72, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPageView' event (evid: 111)
 *
 * Event description: Every page opened in the App Market will trigger this event.
 *
 * @param {appMarketPageViewParams} params - The parameters for this event
 * @property {string} app_id - if app page opened, app_id. Else = null
 * @property {number} app_review_count - how many reviews were posted for this app at this point
 * @property {number} app_review_score - the current app score when a app page is loaded. (multiplied by 100 due to format limitations). if No score, -1000 will be the score.
 * @property {string} app_subcategory - if app was shown in a category page where sub categories were selected - name them.
 * @property {GUID} collimp_id - collection impression guid
 * @property {GUID} esi
 * @property {boolean} has_app_requirement - true\false
 * @property {GUID} impression_id - Impression id - unique for which impression
 * @property {boolean} in_context_view - bool
 * @property {boolean} is_app_dependency - true\false
 * @property {boolean} is_compatible - true\false
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - market type - editor, dashboard, standalone
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} navigation_source - what triggered the pave view? ( menu navigation, search, deep link etc)
 * @property {number} number_of_depended_apps - number_of_depended_apps
 * @property {string} page_name - page name
 * @property {string} referral_info - referral_info (correct, no typo)
 * @property {number} referral_section_index - what is the index number of the app box that was clicked in previous page
 * @property {string} referral_section_name - what is the section name that was clicked in previous page
 * @property {string} referral_sub_tag_name - e.g. sub category
 * @property {string} referral_tag_name - what is the tag_name that referred to this page
 * @property {string} referral_tag_type - previous page type
 * @property {string} refferal_info - refferal_info - is typo
 * @property {string} search_term - search_term used to get here
 * @property {string} section - In which section is that app located?
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} slug_index_list - list of slug_id presented in homepage from top to bottom
 * @property {string} sub_tag_name - sub tag . e.g. sub-categories
 * @property {string} tag_name - the name of the tag type (if collection - must have apps, free apps etc. if category - music, video , business etc)
 * @property {string} tag_type - tag type - collection, category
 * @property {string} utm_id - campaign source
 * @property {string} vertical
 * @property {string} widget_referral - is flow referred from widget
 * @return {LogObject<appMarketPageViewParams>} LogObject<appMarketPageViewParams>
 */
export function appMarketPageView(params) {
    return { evid: 111, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPendingForDeleteModalLoaded' event (evid: 127)
 *
 * Event description: when user is trying to delete an editor component app from the BM , he will get this modal to ask him to move to Editor.
 *
 * @param {appMarketPendingForDeleteModalLoadedParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketPendingForDeleteModalLoadedParams>} LogObject<appMarketPendingForDeleteModalLoadedParams>
 */
export function appMarketPendingForDeleteModalLoaded(params) {
    return { evid: 127, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketConsentPageAllowAndInstall117' event (evid: 117)
 *
 * Event description: This event was never used. can be used for new events.
 *
 * @param {appMarketConsentPageAllowAndInstall117Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {GUID} esi
 * @property {string} marketVersion - new market or old market
 * @property {GUID} msid - msid
 * @property {string} vertical - what is the vertical of the site
 * @return {LogObject<appMarketConsentPageAllowAndInstall117Params>} LogObject<appMarketConsentPageAllowAndInstall117Params>
 */
export function appMarketConsentPageAllowAndInstall117(params) {
    return { evid: 117, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPendingAppConsentModalLoaded' event (evid: 1104)
 *
 * Event description: When users tries to use an app that was installed but not all the way properly (so its in pending mode). the app will prompt the user to finish the installation first.
 *
 * @param {appMarketPendingAppConsentModalLoadedParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - market
 * @property {GUID} msid - msid
 * @property {string} step_name - 'modal opened' or 'click on start'
 * @property {string} vertical
 * @return {LogObject<appMarketPendingAppConsentModalLoadedParams>} LogObject<appMarketPendingAppConsentModalLoadedParams>
 */
export function appMarketPendingAppConsentModalLoaded(params) {
    return { evid: 1104, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketDowngradeGoToDowngradeFromDeleteModal' event (evid: 408)
 *
 * Event description: Go to Downgrade from Delete Modal
 *
 * @param {appMarketDowngradeGoToDowngradeFromDeleteModalParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {GUID} instance_id - instance id
 * @property {string} market - market
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketDowngradeGoToDowngradeFromDeleteModalParams>} LogObject<appMarketDowngradeGoToDowngradeFromDeleteModalParams>
 */
export function appMarketDowngradeGoToDowngradeFromDeleteModal(params) {
    return { evid: 408, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketStandaloneHomepageClicksOnStartNow' event (evid: 139)
 *
 * Event description: user enters the standalone homepage and clicks on start now (link to dev center).
 *
 * @param {appMarketStandaloneHomepageClicksOnStartNowParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - standalone
 * @property {GUID} msid - msid
 * @property {string} page_name - page name
 * @property {string} tag_type - home page
 * @property {string} vertical
 * @return {LogObject<appMarketStandaloneHomepageClicksOnStartNowParams>} LogObject<appMarketStandaloneHomepageClicksOnStartNowParams>
 */
export function appMarketStandaloneHomepageClicksOnStartNow(params) {
    return { evid: 139, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'externalPpCheckoutStart' event (evid: 77)
 *
 * Event description: Checkout Started Package Picker
 *
 * @param {externalPpCheckoutStartParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {string} app_plan_id - plan_id of app
 * @property {GUID} appstoreId - unique guid for app_id, package and cycle. (e.g - App_1 , pro package, yearly cycle)
 * @property {number} payment_cycle - 3 - monthly, 6- yearly etc.
 * @property {string} cycle_name - year, month etc
 * @property {GUID} esi
 * @property {string} flow_type - upgrade or downgrade
 * @property {GUID} msid - msid
 * @property {number} num_of_packages - number of packages offered by the app to upgrade.
 * @property {string} pp_origin - Where did the package picker opened from
 * @property {Url} referer - url that upgrade came from
 * @property {string} referralAdditionalInfo - Which button sent the user into upgrading the TPA funnel (ed_Settings dialog - favicon_upgrade_btn, ed_Settings dialog - url_upgrade_btn)
 * @property {string} sale_name - nov_21
 * @property {string} vendorProductId - vendor product id
 * @property {string} vertical
 * @return {LogObject<externalPpCheckoutStartParams>} LogObject<externalPpCheckoutStartParams>
 */
export function externalPpCheckoutStart(params) {
    return { evid: 77, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__85' event (evid: 85)
 *
 * Event description: review system -> sort_by click
 *
 * @param {__deprecated__85Params} params - The parameters for this event
 * @property {GUID} product_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} value - {most_recent, most_liked, highest_rate, lowest_rate}
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__85Params>} LogObject<__deprecated__85Params>
 */
export function __deprecated__85(params) {
    return { evid: 85, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__65' event (evid: 65)
 *
 * Event description: Going back in manage apps window
 *
 * @param {__deprecated__65Params} params - The parameters for this event
 * @property {GUID} esi - esi
 * @property {string} market - editor, dashboard etc.
 * @property {string} vertical
 * @return {LogObject<__deprecated__65Params>} LogObject<__deprecated__65Params>
 */
export function __deprecated__65(params) {
    return { evid: 65, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketOpenAppCategory' event (evid: 112)
 *
 * Event description: Open an entire app category for farther browsing
 *
 * @param {appMarketOpenAppCategoryParams} params - The parameters for this event
 * @property {string} app_category - App category name (fitness, photography, marketing etc)
 * @property {string} category_tree - is category or subcategory
 * @property {GUID} esi
 * @property {number} index - ranking order of see-more-apps click from top to bottom (1 is first, 2 is 2nd, 3 is 3rd etc).
 * @property {string} market - market type - editor, dashboard, standalone
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {string} pageType - Type of page viewed ( Homepage, App page, Developer page , collection page etc)
 * @property {string} vertical
 * @return {LogObject<appMarketOpenAppCategoryParams>} LogObject<appMarketOpenAppCategoryParams>
 */
export function appMarketOpenAppCategory(params) {
    return { evid: 112, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__102' event (evid: 102)
 *
 * Event description: click back
 *
 * @param {__deprecated__102Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {string} source_page - {home_page/search}
 * @property {string} target_page - {home_page/search}
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__102Params>} LogObject<__deprecated__102Params>
 */
export function __deprecated__102(params) {
    return { evid: 102, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageOneDependencyClick' event (evid: 1854)
 *
 * Event description: app page one dependency click
 *
 * @param {appMarketAppPageOneDependencyClickParams} params - The parameters for this event
 * @property {string} app_dependency - app dependency id
 * @property {string} app_id - app_id
 * @property {string} click_type - go to editor\ app
 * @property {string} dependency_type - app\ geo\ language
 * @property {GUID} esi
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - market
 * @property {string} referral_section_name - what is the section name that was clicked in previous page
 * @property {string} refferal_info - referral_info
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} type - one, multiple, pending
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageOneDependencyClickParams>} LogObject<appMarketAppPageOneDependencyClickParams>
 */
export function appMarketAppPageOneDependencyClick(params) {
    return { evid: 1854, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketOpenThirdPartyAppBackOffice' event (evid: 1850)
 *
 * Event description: Open the App Backoffice from e-container modal.
 *
 * @param {appMarketOpenThirdPartyAppBackOfficeParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {boolean} is_pending_install - is the app in a pending install state?
 * @property {string} market - dashboard, editor... etc
 * @property {GUID} msid - msid
 * @property {string} page_name - name of page opened
 * @property {string} referral_info - where was the app referred from? (last page)
 * @property {string} vertical
 * @return {LogObject<appMarketOpenThirdPartyAppBackOfficeParams>} LogObject<appMarketOpenThirdPartyAppBackOfficeParams>
 */
export function appMarketOpenThirdPartyAppBackOffice(params) {
    return { evid: 1850, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketEditorRedirectModal' event (evid: 177)
 *
 * Event description: User is shown the redirect to editor modal
 *
 * @param {appMarketEditorRedirectModalParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} flow_type - consent or update_app_version
 * @property {string} market - editor, dashboard market?
 * @property {GUID} msid - msid
 * @property {number} num_of_permissions - number of permissions requested by the installer
 * @property {string} referral_info - referral_info
 * @property {string} vertical
 * @return {LogObject<appMarketEditorRedirectModalParams>} LogObject<appMarketEditorRedirectModalParams>
 */
export function appMarketEditorRedirectModal(params) {
    return { evid: 177, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPackagePickerNavigation' event (evid: 1102)
 *
 * Event description: Event is triggered whenever the user navigates between the screens in the PP (select a plan/ select a billing cycle/ complete your purchase)
 *
 * @param {appMarketPackagePickerNavigationParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} buttonType - back/breadcrumbs
 * @property {GUID} esi
 * @property {string} from_tab - navigation origin
 * @property {GUID} msid - msid
 * @property {string} to_tab - navigation destination
 * @property {string} vertical
 * @return {LogObject<appMarketPackagePickerNavigationParams>} LogObject<appMarketPackagePickerNavigationParams>
 */
export function appMarketPackagePickerNavigation(params) {
    return { evid: 1102, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1010' event (evid: 1010)
 *
 * Event description: Split page loaded (create test app / submit a proposal for your app)
 *
 * @param {__deprecated__1010Params} params - The parameters for this event
 * @property {GUID} esi - session id
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1010Params>} LogObject<__deprecated__1010Params>
 */
export function __deprecated__1010(params) {
    return { evid: 1010, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketCancelAppReviewClick' event (evid: 108)
 *
 * Event description: Clicking on cancel when review page is open for apps.
 *
 * @param {appMarketCancelAppReviewClickParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} tag_type - tag_type
 * @property {string} vertical
 * @return {LogObject<appMarketCancelAppReviewClickParams>} LogObject<appMarketCancelAppReviewClickParams>
 */
export function appMarketCancelAppReviewClick(params) {
    return { evid: 108, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketBackToManageApps136' event (evid: 136)
 *
 * Event description: User from app market dashboard clicks on back to manage apps button.
 *
 * @param {appMarketBackToManageApps136Params} params - The parameters for this event
 * @property {GUID} appId - null if not exists
 * @property {GUID} esi
 * @property {string} market - what is the market (suppose to be only dashboard)
 * @property {GUID} msid - msid
 * @property {string} tag_name - tag name
 * @property {string} tag_type - (homepage, app page, collection etc)
 * @property {string} vertical
 * @return {LogObject<appMarketBackToManageApps136Params>} LogObject<appMarketBackToManageApps136Params>
 */
export function appMarketBackToManageApps136(params) {
    return { evid: 136, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1033' event (evid: 1033)
 *
 * Event description: Click next in the "intro" phase of the app proposal flow
 *
 * @param {__deprecated__1033Params} params - The parameters for this event
 * @property {string} app_kind - Kind of app (travel, weddings, etc. - according to values in drop-down menu)
 * @property {Url} app_link - App's website
 * @property {string} app_name - Name of app
 * @property {string} app_platform - Other platforms the app is integrated with
 * @property {GUID} esi - session id
 * @property {string} phaseName - '2_intro'
 * @property {GUID} proposal_id - Unique ID for each proposal
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1033Params>} LogObject<__deprecated__1033Params>
 */
export function __deprecated__1033(params) {
    return { evid: 1033, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__20' event (evid: 20)
 *
 * Event description: click on open collection view
 *
 * @param {__deprecated__20Params} params - The parameters for this event
 * @property {string} category - {[market]_collection}
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {string} origin - {ed_home}
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__20Params>} LogObject<__deprecated__20Params>
 */
export function __deprecated__20(params) {
    return { evid: 20, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppSupportedGeoSHover' event (evid: 702)
 *
 * Event description: user hover over app geo supported
 *
 * @param {appMarketAppSupportedGeoSHoverParams} params - The parameters for this event
 * @property {GUID} app_id - guid
 * @property {GUID} esi
 * @property {string} market - editor dashboard
 * @property {string} marketVersion - 'new' else null
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketAppSupportedGeoSHoverParams>} LogObject<appMarketAppSupportedGeoSHoverParams>
 */
export function appMarketAppSupportedGeoSHover(params) {
    return { evid: 702, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'manageAppsOpenContextMenu' event (evid: 94)
 *
 * Event description: Manage Apps section in App Market. click on context menu (...)
 *
 * @param {manageAppsOpenContextMenuParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} esi
 * @property {string} market - what market ? (editor, dashboard etc)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<manageAppsOpenContextMenuParams>} LogObject<manageAppsOpenContextMenuParams>
 */
export function manageAppsOpenContextMenu(params) {
    return { evid: 94, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'marketOpened' event (evid: 11)
 *
 * Event description: market opened
 *
 * @param {marketOpenedParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {boolean} is_first - (per user per site)
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {number} num_pending_apps - {0,1,2...}
 * @property {string} page_name - {'home, 'pending', 'app', 'search'}
 * @property {string} personalized - {general, online_store, music...}
 * @property {string} postreg_category - what is the postreg category of the site
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_info_category - {[add panel 'view more apps' category], [sa category], [cart category]...}
 * @property {GUID} site_id - site_id
 * @property {string} tag_name - name of the specific tag, category name such as 'music' etc
 * @property {string} tag_type - type of tag, 'homepage','app page', 'category page' etc)
 * @property {number} ts - market loading time in ms
 * @property {number} ts_partial - loading time from previous step (after the SSR was loaded) in ms
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<marketOpenedParams>} LogObject<marketOpenedParams>
 */
export function marketOpened(params) {
    return { evid: 11, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketShowHideMenuClick' event (evid: 1105)
 *
 * Event description: The user clicks on Show Menu / Hide menu.
 * menu: BizManager (Dashboard) sidebar menu
 *
 * @param {appMarketShowHideMenuClickParams} params - The parameters for this event
 * @property {string} entry_point - user enter the market from: dashboard_sidebar_menu, search, explore, dashboard_more_than_a_website
 * @property {GUID} esi
 * @property {string} market - dashboard, editor, standalone
 * @property {GUID} msid - msid
 * @property {string} page_type - current page in the App Market: Homepage, manage_apps, app_page, collection, category
 * @property {string} type - type of click: 'show' or 'hide'
 * @property {string} vertical
 * @return {LogObject<appMarketShowHideMenuClickParams>} LogObject<appMarketShowHideMenuClickParams>
 */
export function appMarketShowHideMenuClick(params) {
    return { evid: 1105, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageSectionImpression' event (evid: 1861)
 *
 * Event description: Impression of a section in the app page
 *
 * @param {appMarketAppPageSectionImpressionParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - dashboard, editor, standalone
 * @property {GUID} msid - msid
 * @property {string} section - section in the app page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageSectionImpressionParams>} LogObject<appMarketAppPageSectionImpressionParams>
 */
export function appMarketAppPageSectionImpression(params) {
    return { evid: 1861, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1001' event (evid: 1001)
 *
 * Event description: Click Start Now button on new Dev Center's homepage
 *
 * @param {__deprecated__1001Params} params - The parameters for this event
 * @property {string} button_position - 'top_banner' for top banner, 'stats_banner' for the banner with App Market stats
 * @property {GUID} esi - session ID
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1001Params>} LogObject<__deprecated__1001Params>
 */
export function __deprecated__1001(params) {
    return { evid: 1001, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketNotInUseAppPageMultipleDependencyImpression' event (evid: 1855)
 *
 * Event description: app page multiple dependency impression
 *
 * @param {appMarketNotInUseAppPageMultipleDependencyImpressionParams} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} apps_dependency - app dependency ids
 * @property {GUID} esi
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - market
 * @property {string} referral_section_name - what is the section name that was clicked in previous page
 * @property {string} refferal_info - referral_info
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} vertical
 * @return {LogObject<appMarketNotInUseAppPageMultipleDependencyImpressionParams>} LogObject<appMarketNotInUseAppPageMultipleDependencyImpressionParams>
 */
export function appMarketNotInUseAppPageMultipleDependencyImpression(params) {
    return { evid: 1855, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__48' event (evid: 48)
 *
 * Event description: site selector -> close click
 *
 * @param {__deprecated__48Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} button_text - button_text
 * @property {GUID} esi
 * @property {string} market - {standalone,...}
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_info_category - {featured, new, search....}
 * @property {number} sites_count - {1,2,3...}
 * @property {string} title - title
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__48Params>} LogObject<__deprecated__48Params>
 */
export function __deprecated__48(params) {
    return { evid: 48, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__50' event (evid: 50)
 *
 * Event description: Click before market is loaded, when it's not responsive yet.
 *
 * @param {__deprecated__50Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {GUID} msid - msid
 * @property {string} page_name - {modal, market, etc]
 * @property {GUID} site_id - site_id
 * @property {number} ts - number of ms from SSR to the click (some browsers don't support timing API, then send blank)
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__50Params>} LogObject<__deprecated__50Params>
 */
export function __deprecated__50(params) {
    return { evid: 50, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketEditorRedirectClick' event (evid: 178)
 *
 * Event description: when user is trying to update an editor component app from the BM , he will get this modal to ask him to move to Editor. clicking on action button will send this bi
 *
 * @param {appMarketEditorRedirectClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} flow_type - consent or update_app_version
 * @property {string} market - editor, dashboard market?
 * @property {GUID} msid - msid
 * @property {string} referral_info - referral_info
 * @property {string} vertical
 * @return {LogObject<appMarketEditorRedirectClickParams>} LogObject<appMarketEditorRedirectClickParams>
 */
export function appMarketEditorRedirectClick(params) {
    return { evid: 178, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketWidgetsNavigationClick' event (evid: 140)
 *
 * Event description: user navigate in the widget section to right or left
 *
 * @param {appMarketWidgetsNavigationClickParams} params - The parameters for this event
 * @property {string} direction - 'right' or 'left'
 * @property {GUID} esi
 * @property {string} market - market
 * @property {string} marketVersion - new
 * @property {GUID} msid - msid
 * @property {string} page_name - page name (homepage etc.)
 * @property {string} section - widget name
 * @property {string} tag_name - name of tag
 * @property {string} tag_type - type of tag
 * @property {string} vertical
 * @property {string} widget_type - collection/category
 * @return {LogObject<appMarketWidgetsNavigationClickParams>} LogObject<appMarketWidgetsNavigationClickParams>
 */
export function appMarketWidgetsNavigationClick(params) {
    return { evid: 140, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1031' event (evid: 1031)
 *
 * Event description: Click back in any phase of the app proposal flow
 *
 * @param {__deprecated__1031Params} params - The parameters for this event
 * @property {GUID} esi - session id
 * @property {string} phaseName - '1_company_info', '2_intro', '3_details', '4_app_value', '5_pricing'
 * @property {GUID} proposal_id - Unique ID for each proposal
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1031Params>} LogObject<__deprecated__1031Params>
 */
export function __deprecated__1031(params) {
    return { evid: 1031, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__83' event (evid: 83)
 *
 * Event description: review system -> post review
 *
 * @param {__deprecated__83Params} params - The parameters for this event
 * @property {GUID} product_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {number} rate - {1,2...5}
 * @property {GUID} site_id - site_id
 * @property {string} type - {post,edit}
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__83Params>} LogObject<__deprecated__83Params>
 */
export function __deprecated__83(params) {
    return { evid: 83, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'manageAppsContextMenuAction' event (evid: 96)
 *
 * Event description: Manage Apps - Context Menu Action
 *
 * @param {manageAppsContextMenuActionParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {GUID} appInstanceId - app instance id
 * @property {string} contextAction - action taken inside the context menu within the Manage App section in the app market
 * @property {GUID} esi
 * @property {string} market - what market ? (editor, dashboard etc)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<manageAppsContextMenuActionParams>} LogObject<manageAppsContextMenuActionParams>
 */
export function manageAppsContextMenuAction(params) {
    return { evid: 96, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketInContextAppPageSeeFullDescription153' event (evid: 153)
 *
 * Event description: click on 'see full description' button (in context modal)
 *
 * @param {appMarketInContextAppPageSeeFullDescription153Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, da_widget}
 * @property {GUID} msid - msid
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_section_name - section that referred this
 * @property {GUID} uuid - uuid
 * @property {string} vertical - vertical
 * @return {LogObject<appMarketInContextAppPageSeeFullDescription153Params>} LogObject<appMarketInContextAppPageSeeFullDescription153Params>
 */
export function appMarketInContextAppPageSeeFullDescription153(params) {
    return { evid: 153, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketManageAppsActionRequiredClick' event (evid: 9)
 *
 * Event description: In the Manage Apps tab, when user clicks on an action requirement of an app.
 *
 * @param {appMarketManageAppsActionRequiredClickParams} params - The parameters for this event
 * @property {string} action_cta - cta action (claim app, upgrade app, add info, etc)
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {boolean} is_consent_required - is consent required to update
 * @property {boolean} is_editor_required - is editor required to update
 * @property {string} market - market (dashboard, editor standalone
 * @property {GUID} msid - msid
 * @property {Url} url - article url
 * @property {string} vertical
 * @return {LogObject<appMarketManageAppsActionRequiredClickParams>} LogObject<appMarketManageAppsActionRequiredClickParams>
 */
export function appMarketManageAppsActionRequiredClick(params) {
    return { evid: 9, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketSaleBannerImpressionWebMobile' event (evid: 175)
 *
 * Event description: User viewed a sale banner in the web mobile version
 *
 * @param {appMarketSaleBannerImpressionWebMobileParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - standalone etc.
 * @property {GUID} msid - msid
 * @property {string} sale_name - nov-22 etc.
 * @property {string} tag_type - page name where the banner appeared
 * @property {GUID} uuid
 * @property {string} vertical - site vertical
 * @return {LogObject<appMarketSaleBannerImpressionWebMobileParams>} LogObject<appMarketSaleBannerImpressionWebMobileParams>
 */
export function appMarketSaleBannerImpressionWebMobile(params) {
    return { evid: 175, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1032' event (evid: 1032)
 *
 * Event description: Click next in the "company info" phase of the app proposal flow
 *
 * @param {__deprecated__1032Params} params - The parameters for this event
 * @property {string} company - Name of company
 * @property {Url} company_website - Company's website
 * @property {GUID} esi - session id
 * @property {string} number_of_people - According to the values in the drop-down menu
 * @property {string} phaseName - '1_company_info'
 * @property {GUID} proposal_id - Unique ID for each proposal
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1032Params>} LogObject<__deprecated__1032Params>
 */
export function __deprecated__1032(params) {
    return { evid: 1032, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'externalPpCheckoutSuccess' event (evid: 78)
 *
 * Event description: Checkout succeeded for Package Picker
 *
 * @param {externalPpCheckoutSuccessParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {string} app_plan_id - app plan id
 * @property {string} billing_flow - where the billing took place? (external, internal)
 * @property {string} coupon_name - name of coupon used to get discount or special offer
 * @property {number} payment_cycle - cycle_id = 3 is monthly, 6 is yearly etc.
 * @property {string} cycle_name - name of cycle (e.g - monthly, yearly)
 * @property {GUID} esi
 * @property {string} flow_type - upgrade or downgrade
 * @property {GUID} msid - msid
 * @property {string} pp_origin - where was the upgrade click initiated
 * @property {string} sale_name - nov21
 * @property {string} vendorProductId - vendor product id
 * @property {string} vertical
 * @return {LogObject<externalPpCheckoutSuccessParams>} LogObject<externalPpCheckoutSuccessParams>
 */
export function externalPpCheckoutSuccess(params) {
    return { evid: 78, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'searchSearchFocus' event (evid: 14)
 *
 * Event description: search -> search focus
 *
 * @param {searchSearchFocusParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} search_location - search_location
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<searchSearchFocusParams>} LogObject<searchSearchFocusParams>
 */
export function searchSearchFocus(params) {
    return { evid: 14, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketDemoLinkClicked' event (evid: 100)
 *
 * Event description: when a user clicks on a demo link to an app provided by the developer, from the app page in the app market.
 *
 * @param {appMarketDemoLinkClickedParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {string} category - what is the apps category? {featured, new, search, home, must-have-apps....}
 * @property {Url} demo_link - the external demo app link attached in this section
 * @property {GUID} esi
 * @property {string} market - What market? (dashboard, editor, standalone etc)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} vertical - what is the site postreg? {general, online_store, music...}
 * @return {LogObject<appMarketDemoLinkClickedParams>} LogObject<appMarketDemoLinkClickedParams>
 */
export function appMarketDemoLinkClicked(params) {
    return { evid: 100, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'manageAppsAddApp' event (evid: 98)
 *
 * Event description: Manage Apps - go back to market and add more apps.
 *
 * @param {manageAppsAddAppParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - what market ? (editor, dashboard etc)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} referral_info - 'manage_apps' , 'finance_tab' etc
 * @property {string} vertical
 * @return {LogObject<manageAppsAddAppParams>} LogObject<manageAppsAddAppParams>
 */
export function manageAppsAddApp(params) {
    return { evid: 98, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageMultipleDependencyClick' event (evid: 1856)
 *
 * Event description: app page multiple dependency click
 *
 * @param {appMarketAppPageMultipleDependencyClickParams} params - The parameters for this event
 * @property {string} app_clicked - app_clicked - app_id
 * @property {string} app_id - app_id
 * @property {string} apps_dependency - app dependency ids
 * @property {GUID} esi
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - market
 * @property {string} referral_section_name - what is the section name that was clicked in previous page
 * @property {string} refferal_info - referral_info
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageMultipleDependencyClickParams>} LogObject<appMarketAppPageMultipleDependencyClickParams>
 */
export function appMarketAppPageMultipleDependencyClick(params) {
    return { evid: 1856, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketDeleteModalModalOpened' event (evid: 6)
 *
 * Event description: Delete modal opened to user
 *
 * @param {appMarketDeleteModalModalOpenedParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {string} market - editor or dashboard
 * @property {string} marketVersion - market version ('new' or null)
 * @property {GUID} msid - msid
 * @property {string} origin - click origin from? ('context_menu' etc)
 * @property {string} tag_type - Where did the delete take place from?
 * @property {string} vertical
 * @return {LogObject<appMarketDeleteModalModalOpenedParams>} LogObject<appMarketDeleteModalModalOpenedParams>
 */
export function appMarketDeleteModalModalOpened(params) {
    return { evid: 6, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketInContextAppPageSeeFullDescription174' event (evid: 174)
 *
 * Event description: click on 'see full description' button (in context modal)
 *
 * @param {appMarketInContextAppPageSeeFullDescription174Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {string} market - {editor,dashboard,standalone, da_widget}
 * @property {GUID} msid - msid
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_section_name - section that referred this
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<appMarketInContextAppPageSeeFullDescription174Params>} LogObject<appMarketInContextAppPageSeeFullDescription174Params>
 */
export function appMarketInContextAppPageSeeFullDescription174(params) {
    return { evid: 174, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'manageAppsDeletePendingAppClick' event (evid: 97)
 *
 * Event description: Manage Apps - Delete Pending App Click
 *
 * @param {manageAppsDeletePendingAppClickParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {GUID} appInstanceId - app instance id
 * @property {GUID} esi
 * @property {string} market - what market ? (editor, dashboard etc)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<manageAppsDeletePendingAppClickParams>} LogObject<manageAppsDeletePendingAppClickParams>
 */
export function manageAppsDeletePendingAppClick(params) {
    return { evid: 97, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketWidgetLoaded' event (evid: 123)
 *
 * Event description: Widget is loaded to user, for example - recommended apps widget in the business manager homepage.
 *
 * @param {appMarketWidgetLoadedParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {GUID} msid - msid
 * @property {string} origin - what is the origin of the widget?
 * @property {string} vertical
 * @property {string} widget_name - what is the widget name
 * @return {LogObject<appMarketWidgetLoadedParams>} LogObject<appMarketWidgetLoadedParams>
 */
export function appMarketWidgetLoaded(params) {
    return { evid: 123, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appModalOpenAppModalClick' event (evid: 21)
 *
 * Event description: User clicks on app cube to get more info
 *
 * @param {appModalOpenAppModalClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} badge_name - badge
 * @property {string} category - {featured, new, search, pending, all_carousel, my_apps....}
 * @property {GUID} collimp_id - collection impression guid
 * @property {GUID} esi
 * @property {GUID} impression_id - Impression id - unique for which impression
 * @property {number} index - The listing ranking of the number in the presented page.
 * @property {boolean} is_app_dependency - true\false
 * @property {boolean} is_compatible - true\false
 * @property {string} market - {editor,dashboard,standalone, da_widget, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} navigation_source - from which navigation source was the click [home(homepage search bar), sidebar, search (dashboard), editor_search....]
 * @property {number} number_of_depended_apps - number_of_depended_apps
 * @property {string} page_name - page name
 * @property {string} price_label_eng - Free, starting from 4 GBP / month
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_info_category - {[add panel 'view more apps' category], [sa category], [cart category]...}
 * @property {string} referral_sub_tag_name - e.g. sub category name
 * @property {string} referral_tag_name - referral additional info
 * @property {string} referral_tag_type - the previous page type
 * @property {string} search_term - {html, analytics,...}
 * @property {string} section - In which section is that app located?
 * @property {number} section_index - from top = 0, to last = 0+n. what is the ranking order of the section presented?
 * @property {string} section_name - what is the title in the section presented.
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} sub_tag_name - for sub tag such as sub-category in category
 * @property {string} tag_name - unique name of actual tag ('must have apps', 'photography' etc)
 * @property {string} tag_type - category, collection, homepage etc.
 * @property {string} utm_id - campaign source
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @property {string} widget_name - If user clicked on an app cube that was located inside a widget. what is the widget name, else null
 * @property {string} widget_referral - is flow referred from widget
 * @return {LogObject<appModalOpenAppModalClickParams>} LogObject<appModalOpenAppModalClickParams>
 */
export function appModalOpenAppModalClick(params) {
    return { evid: 21, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketReviewImpression' event (evid: 160)
 *
 * Event description: reviews impression- review impressions are visible in the app page.
 *
 * @param {appMarketReviewImpressionParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {GUID} collimp_id - collection impression id (guid)
 * @property {boolean} dev_comment - did the developer commented on the review?
 * @property {GUID} esi
 * @property {string} market - dashboard, editor, standalone
 * @property {GUID} msid - msid
 * @property {string} referral_info - what is the referral that referred to the app page
 * @property {string} referral_name - home page, collections,  category...
 * @property {string} referral_tag_name - what is the tag_name that referred to this install
 * @property {string} review_body - the body of the review, what was written
 * @property {number} review_index - the index of the review: 0 is the first review that will shown in the app page
 * @property {number} reviewsScore - review score: 1-5
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} tag_name - what is the specific tag from the specific app page
 * @property {string} tag_type - app page...
 * @property {string} vertical
 * @return {LogObject<appMarketReviewImpressionParams>} LogObject<appMarketReviewImpressionParams>
 */
export function appMarketReviewImpression(params) {
    return { evid: 160, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppCollectionImpressions' event (evid: 128)
 *
 * Event description: Section
 *
 * @param {appMarketAppCollectionImpressionsParams} params - The parameters for this event
 * @property {string} apps_list - list of app_id by the order they are presented from top left, seperated by ","
 * @property {number} collection_index - what is the index of the collection in the page (from top to bottom)
 * @property {GUID} collimp_id - collection impression id (guid)
 * @property {GUID} esi
 * @property {string} market - market type - editor, dashboard, standalone
 * @property {GUID} msid - msid
 * @property {number} number_of_apps - how many apps presented in the collection
 * @property {string} page_name - page name
 * @property {string} prediction_type - is it static, or using the data science model to choose apps shown
 * @property {string} tag_name - name of the specific tag (must have apps, music apps, free collection etc).
 * @property {string} tag_type - collection, category
 * @property {string} vertical
 * @return {LogObject<appMarketAppCollectionImpressionsParams>} LogObject<appMarketAppCollectionImpressionsParams>
 */
export function appMarketAppCollectionImpressions(params) {
    return { evid: 128, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__18' event (evid: 18)
 *
 * Event description: welcome screen -> internal click
 *
 * @param {__deprecated__18Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} value - {get_social, sell_online,...}
 * @property {string} vertical
 * @return {LogObject<__deprecated__18Params>} LogObject<__deprecated__18Params>
 */
export function __deprecated__18(params) {
    return { evid: 18, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__49' event (evid: 49)
 *
 * Event description: site selector -> select site
 *
 * @param {__deprecated__49Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} button_text - button_text
 * @property {GUID} esi
 * @property {string} market - {standalone,...}
 * @property {GUID} msid - msid
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_info_category - {featured, new, search....}
 * @property {GUID} site_id - site_id
 * @property {number} sites_count - {1,2,3...}
 * @property {string} title - title
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__49Params>} LogObject<__deprecated__49Params>
 */
export function __deprecated__49(params) {
    return { evid: 49, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketLikeUnlikeAppReview' event (evid: 110)
 *
 * Event description: AppMarket - Like /Dislike App Review
 *
 * @param {appMarketLikeUnlikeAppReviewParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} esi
 * @property {string} helpful_click - like / unlike
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketLikeUnlikeAppReviewParams>} LogObject<appMarketLikeUnlikeAppReviewParams>
 */
export function appMarketLikeUnlikeAppReview(params) {
    return { evid: 110, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1036' event (evid: 1036)
 *
 * Event description: Click submit proposal in the "pricing" phase of the app proposal flow
 *
 * @param {__deprecated__1036Params} params - The parameters for this event
 * @property {string} app_price - App requested price - according to values in drop-down menu
 * @property {GUID} esi - session id
 * @property {string} monetization_method - Monetization method for the app ('freemium', 'free_trial', etc. - according to values in drop down menu)
 * @property {string} phaseName - '5_pricing'
 * @property {GUID} proposal_id - Unique ID for each proposal
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1036Params>} LogObject<__deprecated__1036Params>
 */
export function __deprecated__1036(params) {
    return { evid: 1036, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageAppRequirementClick' event (evid: 172)
 *
 * Event description: App Market - App Page - Click on Reviews Number
 *
 * @param {appMarketAppPageAppRequirementClickParams} params - The parameters for this event
 * @property {string} action - other countries, 'site requirement'
 * @property {string} app_id - app id
 * @property {GUID} esi
 * @property {string} market - editor, dashboard, standalone
 * @property {GUID} msid - msid of site
 * @property {string} tag_name - name of the tag
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageAppRequirementClickParams>} LogObject<appMarketAppPageAppRequirementClickParams>
 */
export function appMarketAppPageAppRequirementClick(params) {
    return { evid: 172, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'permissionsPermissionsModalLoaded' event (evid: 41)
 *
 * Event description: permissions -> permissions modal loaded
 *
 * @param {permissionsPermissionsModalLoadedParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} collimp_id - collection impression guid
 * @property {string} consentVersion - old_consent, new_consent
 * @property {GUID} esi
 * @property {string} flow_type - consent or update_app_version
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - editor, dashboard, adi, third_party
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {string} modalType - consent , existing
 * @property {GUID} msid - msid
 * @property {number} num_of_permissions - number of permissions requested by consent modal
 * @property {string} referral_info - referral_info
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<permissionsPermissionsModalLoadedParams>} LogObject<permissionsPermissionsModalLoadedParams>
 */
export function permissionsPermissionsModalLoaded(params) {
    return { evid: 41, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'tpaNewPremiumFlowCyclePageOpened' event (evid: 401)
 *
 * Event description: Event is triggered whenever the cycle choosing page (subscription) is opened
 *
 * @param {tpaNewPremiumFlowCyclePageOpenedParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} app_plan_id - plan_id of app
 * @property {GUID} app_site_id - app_site_id. the instance id. key for app in msid
 * @property {GUID} esi
 * @property {string} flow_type - upgrade or downgrade
 * @property {string} market - source of click for upgrade (editor, dashboard)
 * @property {GUID} msid - msid
 * @property {string} package_id - the package id
 * @property {string} package_name - the package name the string itself
 * @property {string} pp_origin - pp_origin {settings,GFPP,BM}
 * @property {string} sale_name - nov21
 * @property {string} vertical
 * @return {LogObject<tpaNewPremiumFlowCyclePageOpenedParams>} LogObject<tpaNewPremiumFlowCyclePageOpenedParams>
 */
export function tpaNewPremiumFlowCyclePageOpened(params) {
    return { evid: 401, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppImpression' event (evid: 113)
 *
 * Event description: When an app is loaded and visible to user, meaning it was exposed for the user to engage with.
 *
 * @param {appMarketAppImpressionParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {number} app_review_count - total number of reviews in the parentheses
 * @property {number} app_review_score - the score shown next to the app name
 * @property {string} app_subcategory - if app was shown in a category page where sub categories were selected - name them.
 * @property {string} badge_name - name of badge of app box (e.g - Premium Benefit)
 * @property {GUID} collimp_id - collection impression id
 * @property {GUID} esi
 * @property {GUID} impression_id - impression id - unique for which impression
 * @property {string} impression_type - type of exposure of app (app cube, search suggestion etc)
 * @property {number} index - The listing ranking of the number in the presented page.
 * @property {boolean} is_app_dependency - true\false
 * @property {boolean} is_compatible - true\false
 * @property {string} market - market type - editor, dashboard, standalone
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {number} number_of_depended_apps - number_of_depended_apps
 * @property {string} page_name - page name
 * @property {string} price_label_eng - Free, starting from 4 GBP / month
 * @property {string} search_term - search_term used to expose this app
 * @property {string} section - section in which app box is located
 * @property {number} section_index - from top = 0, to last = 0+n. what is the ranking order of the section presented?
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} tag_name - name of the specific tag (must have apps, music apps, free collection etc).
 * @property {string} tag_type - collection, category
 * @property {string} vertical
 * @return {LogObject<appMarketAppImpressionParams>} LogObject<appMarketAppImpressionParams>
 */
export function appMarketAppImpression(params) {
    return { evid: 113, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__37' event (evid: 37)
 *
 * Event description: my apps -> my apps page view
 *
 * @param {__deprecated__37Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {boolean} is_first - (per user+site)
 * @property {string} market - {dashboard}
 * @property {GUID} msid - msid
 * @property {number} num_pending_apps - {0,1,2...}
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_info_category - {featured, new, search....}
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__37Params>} LogObject<__deprecated__37Params>
 */
export function __deprecated__37(params) {
    return { evid: 37, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'openAppClick' event (evid: 33)
 *
 * Event description: click on open app button
 *
 * @param {openAppClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} category - category
 * @property {string} cta_type - ['open', 'add_again']
 * @property {GUID} esi
 * @property {number} index - The listing ranking of the number in the presented page.
 * @property {number} item_position - {1,2,3...}
 * @property {string} market - {editor,dashboard,standalone, da_widget}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {string} method - {direct_add , modal}
 * @property {GUID} msid - msid
 * @property {string} pageType - type of page
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} search_term - {html, analytics,...}
 * @property {string} section - In which section is that app located?
 * @property {GUID} site_id - site_id
 * @property {string} tag_name - name of the specific tag ('must have apps' , 'music' etc)
 * @property {string} tag_type - type of page opened - category, homepage, app page etc
 * @property {string} target - {editor, dashboard}
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<openAppClickParams>} LogObject<openAppClickParams>
 */
export function openAppClick(params) {
    return { evid: 33, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketUpdateSuccessMessageModalOpened' event (evid: 180)
 *
 * Event description: The update success modal shown
 *
 * @param {appMarketUpdateSuccessMessageModalOpenedParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - editor, dashboard market?
 * @property {GUID} msid - msid
 * @property {string} referral_info - referral_info
 * @property {string} vertical
 * @return {LogObject<appMarketUpdateSuccessMessageModalOpenedParams>} LogObject<appMarketUpdateSuccessMessageModalOpenedParams>
 */
export function appMarketUpdateSuccessMessageModalOpened(params) {
    return { evid: 180, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__87' event (evid: 87)
 *
 * Event description: review system -> like click
 *
 * @param {__deprecated__87Params} params - The parameters for this event
 * @property {GUID} product_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {string} type - {like, dislike}
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__87Params>} LogObject<__deprecated__87Params>
 */
export function __deprecated__87(params) {
    return { evid: 87, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPostAppReview' event (evid: 106)
 *
 * Event description: Posting an app review
 *
 * @param {appMarketPostAppReviewParams} params - The parameters for this event
 * @property {string} actionType - add_review, edit_review
 * @property {GUID} appId - app_id
 * @property {string} appScore - ( 1,2,3,4,5 ) , what is the score that user gave the app
 * @property {GUID} esi
 * @property {boolean} has_description - Is it only title? or also description?
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} post_type - is a new review post, or a comment on a review
 * @property {string} review_body - review_body
 * @property {GUID} review_id - what is the id of the review
 * @property {string} review_title - title of the review given by the user that post the review
 * @property {string} tag_type - tag_type
 * @property {string} vertical
 * @return {LogObject<appMarketPostAppReviewParams>} LogObject<appMarketPostAppReviewParams>
 */
export function appMarketPostAppReview(params) {
    return { evid: 106, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__66' event (evid: 66)
 *
 * Event description: Business Manager App Market - Delete App Click
 *
 * @param {__deprecated__66Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {GUID} esi - esi
 * @property {string} market - editor, dashboard etc.
 * @property {string} vertical
 * @return {LogObject<__deprecated__66Params>} LogObject<__deprecated__66Params>
 */
export function __deprecated__66(params) {
    return { evid: 66, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__47' event (evid: 47)
 *
 * Event description: site selector -> site selector loaded
 *
 * @param {__deprecated__47Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} button_text - button_text
 * @property {GUID} esi
 * @property {string} market - {standalone,...}
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_info_category - {featured, new, search....}
 * @property {number} sites_count - {1,2,3...}
 * @property {string} title - title
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__47Params>} LogObject<__deprecated__47Params>
 */
export function __deprecated__47(params) {
    return { evid: 47, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__91' event (evid: 91)
 *
 * Event description: unification -> (server) app added to pending
 *
 * @param {__deprecated__91Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, da_widget}
 * @property {string} marketVersion - market = 'new' if new market, else null
 * @property {GUID} msid - msid
 * @property {string} pending_source - {unification_modal, pending_hybrid, cart}
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} version_name
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__91Params>} LogObject<__deprecated__91Params>
 */
export function __deprecated__91(params) {
    return { evid: 91, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketClickOnCompleteSetup' event (evid: 145)
 *
 * Event description: User clicks 'Complete Setup'
 *
 * @param {appMarketClickOnCompleteSetupParams} params - The parameters for this event
 * @property {string} app_id
 * @property {GUID} esi
 * @property {string} market - market
 * @property {string} referral_info - referral info
 * @property {string} vertical
 * @return {LogObject<appMarketClickOnCompleteSetupParams>} LogObject<appMarketClickOnCompleteSetupParams>
 */
export function appMarketClickOnCompleteSetup(params) {
    return { evid: 145, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPendingForDeleteModalCloseModal' event (evid: 124)
 *
 * Event description: when user is trying to delete an editor component app from the BM , he will get this modal to ask him to move to Editor.closing this modal will send this bi event
 *
 * @param {appMarketPendingForDeleteModalCloseModalParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketPendingForDeleteModalCloseModalParams>} LogObject<appMarketPendingForDeleteModalCloseModalParams>
 */
export function appMarketPendingForDeleteModalCloseModal(params) {
    return { evid: 124, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1101' event (evid: 1101)
 *
 * Event description: Type in a search string in the search bar
 *
 * @param {__deprecated__1101Params} params - The parameters for this event
 * @property {GUID} esi - Session ID
 * @property {string} search_term - The string the user searched
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1101Params>} LogObject<__deprecated__1101Params>
 */
export function __deprecated__1101(params) {
    return { evid: 1101, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketMobileAlertForInstallMoveToDesktop' event (evid: 129)
 *
 * Event description: User try to install an app from the  mobile standalone appmarket and we open a modal to tell him to move to desktop.
 *
 * @param {appMarketMobileAlertForInstallMoveToDesktopParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketMobileAlertForInstallMoveToDesktopParams>} LogObject<appMarketMobileAlertForInstallMoveToDesktopParams>
 */
export function appMarketMobileAlertForInstallMoveToDesktop(params) {
    return { evid: 129, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketSortReviews' event (evid: 109)
 *
 * Event description: Sorting apps reviews
 *
 * @param {appMarketSortReviewsParams} params - The parameters for this event
 * @property {GUID} appId - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} value - {most_recent, most_liked, highest_rate, lowest_rate}
 * @property {string} vertical
 * @return {LogObject<appMarketSortReviewsParams>} LogObject<appMarketSortReviewsParams>
 */
export function appMarketSortReviews(params) {
    return { evid: 109, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketSpotlightArticle' event (evid: 114)
 *
 * Event description: In the Manage Apps tab, when user is exposed to an action requirement of an app.
 *
 * @param {appMarketSpotlightArticleParams} params - The parameters for this event
 * @property {string} action_cta - cta action (claim app, upgrade app, add info, etc)
 * @property {GUID} app_id
 * @property {GUID} esi
 * @property {string} market - market (dashboard, editor standalone
 * @property {GUID} msid - msid
 * @property {Url} url - article url
 * @property {string} vertical
 * @return {LogObject<appMarketSpotlightArticleParams>} LogObject<appMarketSpotlightArticleParams>
 */
export function appMarketSpotlightArticle(params) {
    return { evid: 114, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1020' event (evid: 1020)
 *
 * Event description: Get ready for app proposal page loaded
 *
 * @param {__deprecated__1020Params} params - The parameters for this event
 * @property {GUID} esi - session id
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1020Params>} LogObject<__deprecated__1020Params>
 */
export function __deprecated__1020(params) {
    return { evid: 1020, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAddAppDisableHover' event (evid: 1858)
 *
 * Event description: disable hover and message shows
 *
 * @param {appMarketAddAppDisableHoverParams} params - The parameters for this event
 * @property {string} all_requirements_filled - yes/no/no-requirements
 * @property {GUID} app_id - app_id
 * @property {string} category - Every app belongs to one category. which one is the app category of this app?
 * @property {string} click_location - 'main_cta' or 'sticky_header'
 * @property {GUID} collimp_id - collection impression guid
 * @property {GUID} esi
 * @property {GUID} impression_id - impression id - unique for which impression
 * @property {boolean} in_context_view - bool
 * @property {number} index - The listing ranking of the number in the presented page.
 * @property {boolean} is_app_dependency - true\false
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - {editor,dashboard,standalone, da_widget}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} navigation_source - e.g. top-search
 * @property {number} number_of_depended_apps - number_of_depended_apps
 * @property {string} pageType - Type of page used when add app was clicked ( Homepage, App page, Developer page , collection page etc)
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {string} referral_section_name - section that referred this
 * @property {string} referral_sub_tag_name - e.g. sub category name
 * @property {string} referral_tag_type - previous page type
 * @property {string} require_type - the unfulfilled requirement (same as prompt message) , 'null' if all fulfilled
 * @property {string} search_term - {html, analytics,...}
 * @property {string} section - In which section is that app located?
 * @property {number} section_index - from top = 0, to last = 0+n. what is the ranking order of the section presented?
 * @property {string} section_name - name of section's title presented
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} tag_name - name of the specific tag ('must have apps' , 'music' etc)
 * @property {string} tag_type - type of page opened - category, homepage, app page etc
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @property {string} widget_name - when trying to add an app, if app the inside a widget - what is the widget name. else null
 * @property {string} widget_referral - is flow referred from widget
 * @return {LogObject<appMarketAddAppDisableHoverParams>} LogObject<appMarketAddAppDisableHoverParams>
 */
export function appMarketAddAppDisableHover(params) {
    return { evid: 1858, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketSupportMailModalClosedX' event (evid: 701)
 *
 * Event description: Support Modal Opened
 *
 * @param {appMarketSupportMailModalClosedXParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_app_installed - true or false
 * @property {string} market - editor or dashboard
 * @property {string} marketVersion - send 'new' for new app market, else null
 * @property {GUID} msid - msid
 * @property {string} origin - origin
 * @property {string} tag_type - app page or manage apps etc
 * @property {string} vertical
 * @return {LogObject<appMarketSupportMailModalClosedXParams>} LogObject<appMarketSupportMailModalClosedXParams>
 */
export function appMarketSupportMailModalClosedX(params) {
    return { evid: 701, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketTerminatorLearnHowClick' event (evid: 169)
 *
 * Event description: When user clicks on 'learn how' after terminator box suggested a feature outside of app market.
 *
 * @param {appMarketTerminatorLearnHowClickParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} experiment_name - what is the subject / name of experiment
 * @property {string} market - market (editor or dashboard)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} search_term - the keyword that was searched
 * @property {string} vertical
 * @return {LogObject<appMarketTerminatorLearnHowClickParams>} LogObject<appMarketTerminatorLearnHowClickParams>
 */
export function appMarketTerminatorLearnHowClick(params) {
    return { evid: 169, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'externalPpCheckoutFailed' event (evid: 79)
 *
 * Event description: Checkout failed Package Picker
 *
 * @param {externalPpCheckoutFailedParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {string} app_plan_id - plan_id of app
 * @property {string} billing_flow - where the billing took place? (external, internal)
 * @property {number} payment_cycle - cycle_id = 3 is monthly, 6 is yearly etc.
 * @property {string} cycle_name - year, month etc
 * @property {GUID} esi
 * @property {string} fail_reason - why checkout was failed
 * @property {string} flow_type - upgrade or downgrade
 * @property {GUID} msid - msid
 * @property {string} vendorProductId - vendor product id
 * @property {string} vertical
 * @return {LogObject<externalPpCheckoutFailedParams>} LogObject<externalPpCheckoutFailedParams>
 */
export function externalPpCheckoutFailed(params) {
    return { evid: 79, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageClickOnReviewsNumber' event (evid: 170)
 *
 * Event description: App Market - App Page - Click on Reviews Number
 *
 * @param {appMarketAppPageClickOnReviewsNumberParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {GUID} esi
 * @property {string} market - editor, dashboard, standalone
 * @property {GUID} msid - msid of site
 * @property {string} tag_name - name of the tag
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageClickOnReviewsNumberParams>} LogObject<appMarketAppPageClickOnReviewsNumberParams>
 */
export function appMarketAppPageClickOnReviewsNumber(params) {
    return { evid: 170, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__71' event (evid: 71)
 *
 * Event description: premium cart -> add app to cart click
 *
 * @param {__deprecated__71Params} params - The parameters for this event
 * @property {string} addition_method - {direct_add, modal}
 * @property {GUID} app_id - app_id
 * @property {string} category - {featured, featured_carousel, business_tools_carousel....}
 * @property {GUID} esi
 * @property {string} market - {premium_cart}
 * @property {string} modal_tab_name - {overview, demo...}
 * @property {GUID} msid - msid
 * @property {string} referral_info - {email_1, email_2,ed_market, da_NF, da_market, domain}
 * @property {string} referral_info_category - {[ed category], [da category]...}
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__71Params>} LogObject<__deprecated__71Params>
 */
export function __deprecated__71(params) {
    return { evid: 71, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketDeleteModalDeleteAppApproved' event (evid: 7)
 *
 * Event description: User clicks on Delete App call to action button
 *
 * @param {appMarketDeleteModalDeleteAppApprovedParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {string} delete_details - Delete reason explained by user (optional)
 * @property {string} delete_reason - what is the chosen reason to delete
 * @property {GUID} esi
 * @property {string} market - market
 * @property {string} marketVersion - market version (new or null)
 * @property {GUID} msid - msid
 * @property {string} origin - click origin from? ('context_menu' etc)
 * @property {number} reason_index - the position of the reason in the list
 * @property {string} tag_type - Where did the delete take place from?
 * @property {string} vertical
 * @return {LogObject<appMarketDeleteModalDeleteAppApprovedParams>} LogObject<appMarketDeleteModalDeleteAppApprovedParams>
 */
export function appMarketDeleteModalDeleteAppApproved(params) {
    return { evid: 7, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__68' event (evid: 68)
 *
 * Event description: Choosing what to do when getting explanation window after clicking 'delete app' (trash can symbol)
 *
 * @param {__deprecated__68Params} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {string} clickType - what was clicked? 'exit','ok'
 * @property {GUID} esi - esi
 * @property {string} market - editor, dashboard etc.
 * @property {string} vertical
 * @return {LogObject<__deprecated__68Params>} LogObject<__deprecated__68Params>
 */
export function __deprecated__68(params) {
    return { evid: 68, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketTpaPricingPlansErrorPageLoaded' event (evid: 147)
 *
 * Event description: Monitoring client side errors during package picker funnel
 *
 * @param {appMarketTpaPricingPlansErrorPageLoadedParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {string} error_cause
 * @property {GUID} esi
 * @property {string} market
 * @property {GUID} msid - msid
 * @property {string} pp_origin - Where did the package picker opened from
 * @property {string} vertical
 * @return {LogObject<appMarketTpaPricingPlansErrorPageLoadedParams>} LogObject<appMarketTpaPricingPlansErrorPageLoadedParams>
 */
export function appMarketTpaPricingPlansErrorPageLoaded(params) {
    return { evid: 147, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketDowngradeOpenModal' event (evid: 405)
 *
 * Event description: downgrade modal opens
 *
 * @param {appMarketDowngradeOpenModalParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {GUID} instance_id - instance id
 * @property {GUID} msid - msid
 * @property {string} pp_origin - package picker click origin
 * @property {string} vertical
 * @return {LogObject<appMarketDowngradeOpenModalParams>} LogObject<appMarketDowngradeOpenModalParams>
 */
export function appMarketDowngradeOpenModal(params) {
    return { evid: 405, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketToastClosedX' event (evid: 152)
 *
 * Event description: user closes the toast presented
 *
 * @param {appMarketToastClosedXParams} params - The parameters for this event
 * @property {string} action_cta - action required
 * @property {string} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - dashboard, editor etc
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketToastClosedXParams>} LogObject<appMarketToastClosedXParams>
 */
export function appMarketToastClosedX(params) {
    return { evid: 152, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketSupportMailModalOpened' event (evid: 669)
 *
 * Event description: Support Modal Opened
 *
 * @param {appMarketSupportMailModalOpenedParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {GUID} instance_id - instance_id
 * @property {boolean} is_app_installed - true or false
 * @property {string} market - editor or dashboard
 * @property {string} marketVersion - send 'new' for new app market, else null
 * @property {GUID} msid - msid
 * @property {string} origin - origin in tag_type
 * @property {string} tag_type - tag of page - app page, manage apps etc.
 * @property {string} vertical
 * @return {LogObject<appMarketSupportMailModalOpenedParams>} LogObject<appMarketSupportMailModalOpenedParams>
 */
export function appMarketSupportMailModalOpened(params) {
    return { evid: 669, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'tpaNewPremiumFlowPpOpened' event (evid: 400)
 *
 * Event description: Event is triggered whenever the PP is opened
 *
 * @param {tpaNewPremiumFlowPpOpenedParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} app_site_id - app_site_id. the instance id. key for app in msid
 * @property {GUID} esi
 * @property {string} flow_type - upgrade or downgrade
 * @property {string} market - source of click (editor, dashboard)
 * @property {GUID} msid - msid
 * @property {number} num_of_packages - num_of_packages presented in the PP
 * @property {string} pp_origin - pp_origin {settings,GFPP,BM}
 * @property {string} sale_name - nov21
 * @property {string} vertical
 * @return {LogObject<tpaNewPremiumFlowPpOpenedParams>} LogObject<tpaNewPremiumFlowPpOpenedParams>
 */
export function tpaNewPremiumFlowPpOpened(params) {
    return { evid: 400, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1030' event (evid: 1030)
 *
 * Event description: App proposal page loaded. phase_name indicates the step number in the app proposal flow
 *
 * @param {__deprecated__1030Params} params - The parameters for this event
 * @property {GUID} esi - session id
 * @property {string} phaseName - '1_company_info', '2_intro', '3_details', '4_app_value', '5_pricing'
 * @property {GUID} proposal_id - Unique ID for each proposal
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1030Params>} LogObject<__deprecated__1030Params>
 */
export function __deprecated__1030(params) {
    return { evid: 1030, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageHover' event (evid: 1860)
 *
 * Event description: User Hovers in the app page
 *
 * @param {appMarketAppPageHoverParams} params - The parameters for this event
 * @property {string} app_id - app_id
 * @property {GUID} esi
 * @property {string} hover_type - availability, app_languages, site_requirments, other_countries
 * @property {string} location - the location of the hover
 * @property {string} market - market
 * @property {GUID} msid - msid
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageHoverParams>} LogObject<appMarketAppPageHoverParams>
 */
export function appMarketAppPageHover(params) {
    return { evid: 1860, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1035' event (evid: 1035)
 *
 * Event description: Click next in the "app value" phase of the app proposal flow
 *
 * @param {__deprecated__1035Params} params - The parameters for this event
 * @property {string} app_uniqueness - What makes the app unique
 * @property {GUID} esi - session id
 * @property {string} how_can_app_help_users - How can the app help Wix users
 * @property {string} phaseName - '4_app_value'
 * @property {GUID} proposal_id - Unique ID for each proposal
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {string} similar_apps - Similar apps in the Market
 * @property {string} target_audience - Target audience for the app
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1035Params>} LogObject<__deprecated__1035Params>
 */
export function __deprecated__1035(params) {
    return { evid: 1035, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPostConsentLoaded' event (evid: 121)
 *
 * Event description: After Consent flow for app install is finished, user will be prompt to take action in some cases. when presented to so , this event will be sent.
 *
 * @param {appMarketPostConsentLoadedParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {boolean} in_context_view - bool
 * @property {string} market - market
 * @property {string} marketVersion - for new market = 'new', else null
 * @property {GUID} msid - msid
 * @property {string} post_consent_type - type of the post conten
 * @property {string} vertical
 * @return {LogObject<appMarketPostConsentLoadedParams>} LogObject<appMarketPostConsentLoadedParams>
 */
export function appMarketPostConsentLoaded(params) {
    return { evid: 121, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__84' event (evid: 84)
 *
 * Event description: review system -> cancel click
 *
 * @param {__deprecated__84Params} params - The parameters for this event
 * @property {GUID} product_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__84Params>} LogObject<__deprecated__84Params>
 */
export function __deprecated__84(params) {
    return { evid: 84, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1034' event (evid: 1034)
 *
 * Event description: Click next in the "details" phase of the app proposal flow
 *
 * @param {__deprecated__1034Params} params - The parameters for this event
 * @property {string} app_description - Description of app
 * @property {string} app_feature_1 - App feature no. 1
 * @property {string} app_feature_2 - App feature no. 2
 * @property {string} app_feature_3 - App feature no. 3
 * @property {GUID} esi - session id
 * @property {string} needed_data - Data needed from Wix for the app to work
 * @property {string} phaseName - '3_details'
 * @property {GUID} proposal_id - Unique ID for each proposal
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1034Params>} LogObject<__deprecated__1034Params>
 */
export function __deprecated__1034(params) {
    return { evid: 1034, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketUpdateSuccess' event (evid: 179)
 *
 * Event description: The update was successful (according to the server)
 *
 * @param {appMarketUpdateSuccessParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - editor, dashboard market?
 * @property {GUID} msid - msid
 * @property {string} referral_info - referral_info
 * @property {string} vertical
 * @return {LogObject<appMarketUpdateSuccessParams>} LogObject<appMarketUpdateSuccessParams>
 */
export function appMarketUpdateSuccess(params) {
    return { evid: 179, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__34' event (evid: 34)
 *
 * Event description: dashboard apps -> remove dashboard app click
 *
 * @param {__deprecated__34Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - {dashboard}
 * @property {GUID} msid - msid
 * @property {string} origin - {my_apps,...}
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__34Params>} LogObject<__deprecated__34Params>
 */
export function __deprecated__34(params) {
    return { evid: 34, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageClickReadMoreDescription' event (evid: 134)
 *
 * Event description: when a user clicks on the "Read More" button (app description), from the app page in the app market.
 *
 * @param {appMarketAppPageClickReadMoreDescriptionParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {string} market - What market? (dashboard, editor, standalone etc)
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageClickReadMoreDescriptionParams>} LogObject<appMarketAppPageClickReadMoreDescriptionParams>
 */
export function appMarketAppPageClickReadMoreDescription(params) {
    return { evid: 134, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__63' event (evid: 63)
 *
 * Event description: App Market - Help  Button [?]
 *
 * @param {__deprecated__63Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - market (dashboard, editor etc)
 * @property {string} vertical
 * @return {LogObject<__deprecated__63Params>} LogObject<__deprecated__63Params>
 */
export function __deprecated__63(params) {
    return { evid: 63, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketSiteRequirementsTagClick' event (evid: 703)
 *
 * Event description: user clicks on site requirements
 *
 * @param {appMarketSiteRequirementsTagClickParams} params - The parameters for this event
 * @property {GUID} app_id - guid
 * @property {GUID} esi
 * @property {string} market - dashboard, editor, standalone
 * @property {string} marketVersion - 'new' else null
 * @property {GUID} msid - msid
 * @property {GUID} required_app_id - required_app_id of tagged app (not the app_id of the app page!)
 * @property {string} vertical
 * @return {LogObject<appMarketSiteRequirementsTagClickParams>} LogObject<appMarketSiteRequirementsTagClickParams>
 */
export function appMarketSiteRequirementsTagClick(params) {
    return { evid: 703, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'externalPpMoreAppsWhenSuccess' event (evid: 88)
 *
 * Event description: Dev Center - when Checkout succeeded External Package Picker - user clicked on 'view more apps'
 *
 * @param {externalPpMoreAppsWhenSuccessParams} params - The parameters for this event
 * @property {GUID} appId - app id
 * @property {GUID} esi
 * @property {GUID} msid - msid
 * @property {string} vendorProductId - vendor product id
 * @property {string} vertical
 * @return {LogObject<externalPpMoreAppsWhenSuccessParams>} LogObject<externalPpMoreAppsWhenSuccessParams>
 */
export function externalPpMoreAppsWhenSuccess(params) {
    return { evid: 88, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'searchSearchResults' event (evid: 15)
 *
 * Event description: User interact with the search bar in the AppMarket. after typing a search term user will either: hit 'enter', choose an 'Autocomplete' option from search results, 'exit' the search.
 *
 * @param {searchSearchResultsParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {GUID} first_result_app_id - [first_result_app_id]
 * @property {string} market - {editor,dashboard,standalone}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {number} num_results - {0,1,2,...}
 * @property {string} search_term - {html, analytics,...}
 * @property {string} search_type - autocomplete , trending, recommended
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<searchSearchResultsParams>} LogObject<searchSearchResultsParams>
 */
export function searchSearchResults(params) {
    return { evid: 15, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketConsentPageAllowAndInstall115' event (evid: 115)
 *
 * Event description: User allows permission to app requests, and proceeds to install app
 *
 * @param {appMarketConsentPageAllowAndInstall115Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} collimp_id - collection impression guid
 * @property {string} consentVersion - new consent, old consent
 * @property {GUID} esi
 * @property {string} flow_type - consent or update_app_version
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - editor, dashboard market?
 * @property {string} marketVersion - new market or old market
 * @property {GUID} msid - msid
 * @property {number} num_of_permissions - number of permissions requested by the installer
 * @property {string} referral_info - referral_info
 * @property {string} vertical - what is the vertical of the site
 * @return {LogObject<appMarketConsentPageAllowAndInstall115Params>} LogObject<appMarketConsentPageAllowAndInstall115Params>
 */
export function appMarketConsentPageAllowAndInstall115(params) {
    return { evid: 115, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketToastClickOnCta' event (evid: 151)
 *
 * @param {appMarketToastClickOnCtaParams} params - The parameters for this event
 * @property {string} action_cta - action required
 * @property {string} app_id - app_id
 * @property {GUID} esi - editor session id
 * @property {string} market - dashboard, editor etc
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketToastClickOnCtaParams>} LogObject<appMarketToastClickOnCtaParams>
 */
export function appMarketToastClickOnCta(params) {
    return { evid: 151, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketUpdateAppApproveUpdate' event (evid: 133)
 *
 * Event description: User approve update in the update modal
 *
 * @param {appMarketUpdateAppApproveUpdateParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {GUID} instance_id - instance_id
 * @property {string} market - market
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketUpdateAppApproveUpdateParams>} LogObject<appMarketUpdateAppApproveUpdateParams>
 */
export function appMarketUpdateAppApproveUpdate(params) {
    return { evid: 133, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketTerminatorPresenteToUser' event (evid: 69)
 *
 * Event description: Wix 'Terminator' is an option that opens when a user is searching the app market for a feature that exists in wix , but not as an app. this will
 *
 * @param {appMarketTerminatorPresenteToUserParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} experiment_name - what is the subject / name of experiment
 * @property {string} market - market (editor or dashboard)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} search_term - the keyword that was searched
 * @property {string} vertical
 * @return {LogObject<appMarketTerminatorPresenteToUserParams>} LogObject<appMarketTerminatorPresenteToUserParams>
 */
export function appMarketTerminatorPresenteToUser(params) {
    return { evid: 69, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageReadMoreReviewsClick' event (evid: 171)
 *
 * Event description: App Market - App Page - Click on Reviews Number
 *
 * @param {appMarketAppPageReadMoreReviewsClickParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {string} click_type - click_type
 * @property {GUID} esi
 * @property {string} market - editor, dashboard, standalone
 * @property {string} marketVersion - new
 * @property {GUID} msid - msid of site
 * @property {string} tag_name - name of the tag
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageReadMoreReviewsClickParams>} LogObject<appMarketAppPageReadMoreReviewsClickParams>
 */
export function appMarketAppPageReadMoreReviewsClick(params) {
    return { evid: 171, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appmarketPackagePickerToggleCycleClick' event (evid: 402)
 *
 * Event description: Event is triggered whenever the user toggles between the cycles shown.
 *
 * @param {appmarketPackagePickerToggleCycleClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} cycle_click - what cycle did the user clicked on
 * @property {GUID} esi
 * @property {string} market - editor, dashboard etc
 * @property {GUID} msid - msid
 * @property {number} number_of_plans - the number of plans in this app page
 * @property {string} vertical
 * @return {LogObject<appmarketPackagePickerToggleCycleClickParams>} LogObject<appmarketPackagePickerToggleCycleClickParams>
 */
export function appmarketPackagePickerToggleCycleClick(params) {
    return { evid: 402, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketClickOnAppReviews' event (evid: 89)
 *
 * Event description: user clicks on app review images in the market, could be the 'star' image or the 'comments' image.
 *
 * @param {appMarketClickOnAppReviewsParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {string} category - what is the apps category? {featured, new, search, home, must-have-apps....}
 * @property {GUID} esi
 * @property {string} market - What market? (dashboard, editor, standalone etc)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} vertical - what is the site postreg? {general, online_store, music...}
 * @return {LogObject<appMarketClickOnAppReviewsParams>} LogObject<appMarketClickOnAppReviewsParams>
 */
export function appMarketClickOnAppReviews(params) {
    return { evid: 89, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketOpenManageApps' event (evid: 4)
 *
 * Event description: when user is opening the manage apps window
 *
 * @param {appMarketOpenManageAppsParams} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - market
 * @property {string} marketVersion - 'new' for new market
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketOpenManageAppsParams>} LogObject<appMarketOpenManageAppsParams>
 */
export function appMarketOpenManageApps(params) {
    return { evid: 4, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__67' event (evid: 67)
 *
 * Event description: standalone -> new to wix banner click
 *
 * @param {__deprecated__67Params} params - The parameters for this event
 * @property {GUID} esi
 * @property {string} market - {standalone,...}
 * @property {string} referral_info - {email_1, email_2, google, ed_market_button, ed_add_panel, da_NF, da_market, da_widget, da_in_app, sa_explore, sa_footer, premium_cart.}
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__67Params>} LogObject<__deprecated__67Params>
 */
export function __deprecated__67(params) {
    return { evid: 67, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppPageScrollPage' event (evid: 130)
 *
 * Event description: How much did the user scrolled down while browsing the app page in the App Market.
 *
 * @param {appMarketAppPageScrollPageParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {GUID} esi
 * @property {string} market - dashboard, editor, standalone
 * @property {string} marketVersion - new
 * @property {GUID} msid - msid
 * @property {string} page_part - what is the part in the page that was exposed ( reviews, pricing plans, overview etc)
 * @property {number} page_part_index - the order of the part from top to bottom (1, 2, ,3 ...)
 * @property {string} tag_name - name of tag, if exists (category - video, music etc. collection - must have apps, free apps ... etc)
 * @property {string} vertical
 * @return {LogObject<appMarketAppPageScrollPageParams>} LogObject<appMarketAppPageScrollPageParams>
 */
export function appMarketAppPageScrollPage(params) {
    return { evid: 130, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketReviewAppNotInstalledAlert' event (evid: 403)
 *
 * Event description: App Market - Review - App Not Installed Alert.
 * user attempt to review an app that has never been previously installed by that user - and will be blocked from posting a review.
 *
 * @param {appMarketReviewAppNotInstalledAlertParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - market
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketReviewAppNotInstalledAlertParams>} LogObject<appMarketReviewAppNotInstalledAlertParams>
 */
export function appMarketReviewAppNotInstalledAlert(params) {
    return { evid: 403, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__1000' event (evid: 1000)
 *
 * Event description: Homepage loaded for the new Dev Center.
 *
 * @param {__deprecated__1000Params} params - The parameters for this event
 * @property {GUID} esi - session id
 * @property {Url} referral_url - URL from which the visitor reached the page
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__1000Params>} LogObject<__deprecated__1000Params>
 */
export function __deprecated__1000(params) {
    return { evid: 1000, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppInstallationFailedClient' event (evid: 998)
 *
 * Event description: App Installation Failed , Client BI for rich data.
 *
 * @param {appMarketAppInstallationFailedClientParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} app_subcategory - what is the app sub-category of the category
 * @property {GUID} esi
 * @property {string} fail_reason - fail reason of installation
 * @property {number} index - When apps are loaded on the screen for a user, what is the ranking order that the app was given
 * @property {string} installType - What type of install? (API, App Builder, Share URL, normal, other)
 * @property {GUID} instance_id - app instance id
 * @property {string} market - editor, dashboard, adi, add-panel
 * @property {GUID} msid - msid
 * @property {string} tag_name - what is the specific tag from which the app was installed from
 * @property {string} tag_type - what is the type of app tag? category, collection etc
 * @property {string} version_of_app - app version
 * @property {string} vertical
 * @return {LogObject<appMarketAppInstallationFailedClientParams>} LogObject<appMarketAppInstallationFailedClientParams>
 */
export function appMarketAppInstallationFailedClient(params) {
    return { evid: 998, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketUpdateAppClickUpdate' event (evid: 131)
 *
 * Event description: user click on upgrade app
 *
 * @param {appMarketUpdateAppClickUpdateParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {GUID} instance_id - instance_id
 * @property {string} market - market
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketUpdateAppClickUpdateParams>} LogObject<appMarketUpdateAppClickUpdateParams>
 */
export function appMarketUpdateAppClickUpdate(params) {
    return { evid: 131, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'categoryClick' event (evid: 12)
 *
 * Event description: Choosing an app category from the categories menu in the app market or in the app page
 *
 * @param {categoryClickParams} params - The parameters for this event
 * @property {GUID} app_id - app_id if this click is on an app page
 * @property {string} category - {new, my_apps,...}
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} origin - {welcome_screen, direct,...} 'more_apps_like_this' = app page
 * @property {string} page_name - homepage etc.
 * @property {GUID} site_id - site_id
 * @property {string} subcategory_name - subcategory name
 * @property {string} tag_name - tag_name
 * @property {string} tag_type - tag type
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @property {string} widget_name
 * @return {LogObject<categoryClickParams>} LogObject<categoryClickParams>
 */
export function categoryClick(params) {
    return { evid: 12, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketUpdateSuccessNotification' event (evid: 181)
 *
 * Event description: The update success notification shown in editor
 *
 * @param {appMarketUpdateSuccessNotificationParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketUpdateSuccessNotificationParams>} LogObject<appMarketUpdateSuccessNotificationParams>
 */
export function appMarketUpdateSuccessNotification(params) {
    return { evid: 181, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketBackToManageApps5' event (evid: 5)
 *
 * Event description: back to manage apps click or to the referral page. referral page  = referral_name
 *
 * @param {appMarketBackToManageApps5Params} params - The parameters for this event
 * @property {string} click_origin - origin of click "back to manage app"
 * @property {GUID} esi
 * @property {string} market - market
 * @property {string} marketVersion - 'new' for new market, else null
 * @property {GUID} msid - msid
 * @property {string} referral_name - what is the referral name? 'manage_apps' , 'finance'
 * @property {string} tag_name - tag name
 * @property {string} tag_type - tag type
 * @property {string} vertical
 * @return {LogObject<appMarketBackToManageApps5Params>} LogObject<appMarketBackToManageApps5Params>
 */
export function appMarketBackToManageApps5(params) {
    return { evid: 5, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAddEditAppReviewClick' event (evid: 107)
 *
 * Event description: User opens the review modal of an app, the app is either currently installed or was previously installed, allowing them to perform these actions. This should also include ‘be the first to rate it’ case.
 *
 * @param {appMarketAddEditAppReviewClickParams} params - The parameters for this event
 * @property {string} actionType - add_review, edit_review
 * @property {GUID} appId - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} post_type - is a new review post, or a comment on a review
 * @property {string} tag_type - tag_type
 * @property {string} vertical
 * @return {LogObject<appMarketAddEditAppReviewClickParams>} LogObject<appMarketAddEditAppReviewClickParams>
 */
export function appMarketAddEditAppReviewClick(params) {
    return { evid: 107, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__104' event (evid: 104)
 *
 * Event description: category click from menu
 *
 * @param {__deprecated__104Params} params - The parameters for this event
 * @property {string} category - {new, my_apps,...}
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {string} origin - {menu}
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__104Params>} LogObject<__deprecated__104Params>
 */
export function __deprecated__104(params) {
    return { evid: 104, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketCloseDialogBoxOfUpgradeWixX' event (evid: 73)
 *
 * Event description: When trying to install Apps that require premium wix site, prompt will be opened, This BI event is sent when box is closed.
 *
 * @param {appMarketCloseDialogBoxOfUpgradeWixXParams} params - The parameters for this event
 * @property {string} app_id - app id
 * @property {GUID} esi
 * @property {string} market - determines which market initiated this event (editor or dashboard)
 * @property {string} marketVersion - For new App Market version, what version of market this BI was sent from
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketCloseDialogBoxOfUpgradeWixXParams>} LogObject<appMarketCloseDialogBoxOfUpgradeWixXParams>
 */
export function appMarketCloseDialogBoxOfUpgradeWixX(params) {
    return { evid: 73, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketUpdateAppOpenUpdateModal' event (evid: 132)
 *
 * Event description: Update Modal - Modal Opened after user clicked on 'update app'
 *
 * @param {appMarketUpdateAppOpenUpdateModalParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {GUID} instance_id - instance_id
 * @property {string} market - market
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketUpdateAppOpenUpdateModalParams>} LogObject<appMarketUpdateAppOpenUpdateModalParams>
 */
export function appMarketUpdateAppOpenUpdateModal(params) {
    return { evid: 132, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__35' event (evid: 35)
 *
 * Event description: dashboard apps -> remove dashboard app 2nd step click
 *
 * @param {__deprecated__35Params} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {string} market - {dashboard}
 * @property {GUID} msid - msid
 * @property {string} origin - {my_apps,...}
 * @property {GUID} site_id - site_id
 * @property {string} step_type - {standard, premium, hybrid}
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__35Params>} LogObject<__deprecated__35Params>
 */
export function __deprecated__35(params) {
    return { evid: 35, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPostConsentAction' event (evid: 122)
 *
 * Event description: After Consent flow for app install is finished, user will be prompt to take action in some cases. when user is taking action in this window, this event will be sent.
 *
 * @param {appMarketPostConsentActionParams} params - The parameters for this event
 * @property {string} action - what is the action that the user clicked on
 * @property {GUID} app_id - app_id
 * @property {GUID} esi
 * @property {boolean} in_context_view - bool
 * @property {string} market - market
 * @property {string} marketVersion - for new market = 'new', else null
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketPostConsentActionParams>} LogObject<appMarketPostConsentActionParams>
 */
export function appMarketPostConsentAction(params) {
    return { evid: 122, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__19' event (evid: 19)
 *
 * Event description: welcome screen -> close
 *
 * @param {__deprecated__19Params} params - The parameters for this event
 * @property {string} closing_method - {x, other}
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone}
 * @property {GUID} msid - msid
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical
 * @return {LogObject<__deprecated__19Params>} LogObject<__deprecated__19Params>
 */
export function __deprecated__19(params) {
    return { evid: 19, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppInstallationSuccess' event (evid: 2)
 *
 * Event description: App Install was successful, Client BI for rich data.
 *
 * @param {appMarketAppInstallationSuccessParams} params - The parameters for this event
 * @property {GUID} app_id - app_id
 * @property {string} app_subcategory - what is the app sub-category of the category
 * @property {GUID} collimp_id - collection impression guid
 * @property {GUID} esi
 * @property {GUID} impression_id - Impression id - unique for which impression
 * @property {boolean} in_context_view - bool
 * @property {number} index - app index number when presented
 * @property {string} installation_source
 * @property {GUID} instance_id - app instance id
 * @property {boolean} is_app_dependency - true\false
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - editor, dashboard, adi, add-panel
 * @property {string} marketVersion - version of app market
 * @property {GUID} msid - msid
 * @property {string} navigation_source - e.g. top-search
 * @property {number} number_of_depended_apps - number_of_depended_apps
 * @property {string} referral_info - referral_info (correct, no typo)
 * @property {number} referral_section_index - what is the index number of the app box that was clicked in previous page
 * @property {string} referral_section_name - what is the section name that was clicked in previous page
 * @property {string} referral_sub_tag_name - e.g. sub category name
 * @property {string} referral_tag_name - what is the tag_name that referred to this install
 * @property {string} referral_tag_type - the previous page if referral_info contains something else
 * @property {string} search_term - word used for search term, led to search result, led to app page and install
 * @property {string} section - In which section is that app located?
 * @property {string} slug_id - id of consistent set of apps
 * @property {number} sum_one - value 1 for ttest sum
 * @property {string} tag_name - what is the specific tag from which the app was installed from
 * @property {string} tag_type - what is the type of app tag? category, collection etc
 * @property {string} version_of_app - app version
 * @property {string} vertical
 * @property {string} widget_referral - is flow referred from widget
 * @return {LogObject<appMarketAppInstallationSuccessParams>} LogObject<appMarketAppInstallationSuccessParams>
 */
export function appMarketAppInstallationSuccess(params) {
    return { evid: 2, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketPendingForDeleteModalGoToEditorClick' event (evid: 120)
 *
 * Event description: when user is trying to delete an editor component app from the BM , he will get this modal to ask him to move to Editor. clicking on action button will send this bi
 *
 * @param {appMarketPendingForDeleteModalGoToEditorClickParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {GUID} msid - msid
 * @property {string} vertical
 * @return {LogObject<appMarketPendingForDeleteModalGoToEditorClickParams>} LogObject<appMarketPendingForDeleteModalGoToEditorClickParams>
 */
export function appMarketPendingForDeleteModalGoToEditorClick(params) {
    return { evid: 120, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketBackButtonClick' event (evid: 135)
 *
 * Event description: User clicks to go back from where he is.
 *
 * @param {appMarketBackButtonClickParams} params - The parameters for this event
 * @property {GUID} appId - null if not in app_id
 * @property {GUID} esi
 * @property {string} location - location of button
 * @property {string} market - what is the market
 * @property {GUID} msid - msid
 * @property {string} tag_name - tag name
 * @property {string} tag_type - (homepage, app page, collection etc)
 * @property {string} vertical
 * @return {LogObject<appMarketBackButtonClickParams>} LogObject<appMarketBackButtonClickParams>
 */
export function appMarketBackButtonClick(params) {
    return { evid: 135, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__82' event (evid: 82)
 *
 * Event description: review system -> add_edit review click
 *
 * @param {__deprecated__82Params} params - The parameters for this event
 * @property {GUID} product_id - app_id
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {string} origin - {tab,link}
 * @property {GUID} site_id - site_id
 * @property {string} type - {post,edit}
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__82Params>} LogObject<__deprecated__82Params>
 */
export function __deprecated__82(params) {
    return { evid: 82, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketDowngradeChangePlanClick' event (evid: 406)
 *
 * Event description: downgrade modal opens
 *
 * @param {appMarketDowngradeChangePlanClickParams} params - The parameters for this event
 * @property {GUID} app_id - app id
 * @property {GUID} esi
 * @property {GUID} instance_id - instance id
 * @property {GUID} msid - msid
 * @property {string} pp_origin - package picker click origin
 * @property {string} vertical
 * @return {LogObject<appMarketDowngradeChangePlanClickParams>} LogObject<appMarketDowngradeChangePlanClickParams>
 */
export function appMarketDowngradeChangePlanClick(params) {
    return { evid: 406, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketHomepageAppDependencyAction' event (evid: 1857)
 *
 * Event description: homepage app dependency action
 *
 * @param {appMarketHomepageAppDependencyActionParams} params - The parameters for this event
 * @property {string} action_type - impression\ click
 * @property {string} app_id - app_id
 * @property {string} app_to_return - app_to_return app id
 * @property {GUID} esi
 * @property {boolean} is_external - not originally from the app market
 * @property {string} market - market
 * @property {string} referral_section_name - what is the section name that was clicked in previous page
 * @property {string} refferal_info - referral_info
 * @property {string} slug_id - id of consistent set of apps
 * @property {string} vertical
 * @return {LogObject<appMarketHomepageAppDependencyActionParams>} LogObject<appMarketHomepageAppDependencyActionParams>
 */
export function appMarketHomepageAppDependencyAction(params) {
    return { evid: 1857, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the '__deprecated__16' event (evid: 16)
 *
 * Event description: click on close collection view
 *
 * @param {__deprecated__16Params} params - The parameters for this event
 * @property {string} category - {[market]_collection}
 * @property {GUID} esi
 * @property {string} market - {editor,dashboard,standalone, premium_cart}
 * @property {GUID} msid - msid
 * @property {string} origin - {ed_home}
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - {general, online_store, music...}
 * @return {LogObject<__deprecated__16Params>} LogObject<__deprecated__16Params>
 */
export function __deprecated__16(params) {
    return { evid: 16, src: 24, endpoint: 'market', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppModalOpened' event (evid: 22)
 *
 * Event description: When a user clicks on an App in the App Market, a modal will be opened to present data about the App (overview, pricing, reviews, developer info.
 *
 * @param {appMarketAppModalOpenedParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} referral_info - referral_info
 * @property {string} referral_info_category - referral_info_category
 * @property {string} section - section
 * @property {string} marketVersion - market_version
 * @property {string} search_term - search_term
 * @property {string} vertical - vertical
 * @property {string} category - category
 * @property {GUID} app_id - app_id
 * @property {number} item_position - item_position
 * @property {GUID} esi - esi
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketAppModalOpenedParams>} LogObject<appMarketAppModalOpenedParams>
 */
export function appMarketAppModalOpened(params) {
    return { evid: 22, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppModalTabClick' event (evid: 23)
 *
 * Event description: Inside an app modal, clicking on tabs (pricing, reviews, developer info, demo etc)
 *
 * @param {appMarketAppModalTabClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {string} tab_name - tab_name
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketAppModalTabClickParams>} LogObject<appMarketAppModalTabClickParams>
 */
export function appMarketAppModalTabClick(params) {
    return { evid: 23, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketDeveloperNameLinkClick' event (evid: 24)
 *
 * Event description: app modal -> dev name link click
 *
 * @param {appMarketDeveloperNameLinkClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketDeveloperNameLinkClickParams>} LogObject<appMarketDeveloperNameLinkClickParams>
 */
export function appMarketDeveloperNameLinkClick(params) {
    return { evid: 24, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSupportInfoClick' event (evid: 25)
 *
 * Event description: app modal -> support info click
 *
 * @param {appMarketSupportInfoClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} info_type - info_type
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketSupportInfoClickParams>} LogObject<appMarketSupportInfoClickParams>
 */
export function appMarketSupportInfoClick(params) {
    return { evid: 25, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppModalShareClick' event (evid: 27)
 *
 * Event description: app modal -> share click
 *
 * @param {appMarketAppModalShareClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} share_type - share_type
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketAppModalShareClickParams>} LogObject<appMarketAppModalShareClickParams>
 */
export function appMarketAppModalShareClick(params) {
    return { evid: 27, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSearchAutocompleteClick' event (evid: 29)
 *
 * Event description: search autocomplete click
 *
 * @param {appMarketSearchAutocompleteClickParams} params - The parameters for this event
 * @property {string} auto_search_term - auto_search_term
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {string} marketVersion - market_version
 * @property {string} search_term - search_term
 * @property {string} vertical - vertical
 * @property {string} category - category
 * @property {GUID} app_id - app_id
 * @property {string} type - type
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketSearchAutocompleteClickParams>} LogObject<appMarketSearchAutocompleteClickParams>
 */
export function appMarketSearchAutocompleteClick(params) {
    return { evid: 29, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketClickOnSiteCategoryPopUp' event (evid: 30)
 *
 * Event description: When changing  the site category (stores, photography, music , fitness, etc - this will change the type of apps shown in the front page.)
 *
 * @param {appMarketClickOnSiteCategoryPopUpParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {string} category - category
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketClickOnSiteCategoryPopUpParams>} LogObject<appMarketClickOnSiteCategoryPopUpParams>
 */
export function appMarketClickOnSiteCategoryPopUp(params) {
    return { evid: 30, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketUnificationAddAppClick' event (evid: 32)
 *
 * Event description: unification add app click
 *
 * @param {appMarketUnificationAddAppClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {string} referral_info - referral_info
 * @property {string} referral_info_category - referral_info_category
 * @property {string} marketVersion - market_version
 * @property {string} search_term - search_term
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {string} category - category
 * @property {GUID} app_id - app_id
 * @property {number} item_position - item_position
 * @property {GUID} esi - esi
 * @property {string} component_type - component_type
 * @property {GUID} msid - msid
 * @property {string} addition_method - addition_method
 * @property {string} value - value
 * @property {string} modal_tab_name - modal_tab_name
 * @return {LogObject<appMarketUnificationAddAppClickParams>} LogObject<appMarketUnificationAddAppClickParams>
 */
export function appMarketUnificationAddAppClick(params) {
    return { evid: 32, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketCloseSiteCategoryPopUp' event (evid: 36)
 *
 * Event description: click on close site category pop up (the pop up that is opened in order to choose what site category is used - e.g business, store, photography etc)
 *
 * @param {appMarketCloseSiteCategoryPopUpParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {string} closing_method - closing_method
 * @property {string} category - category
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketCloseSiteCategoryPopUpParams>} LogObject<appMarketCloseSiteCategoryPopUpParams>
 */
export function appMarketCloseSiteCategoryPopUp(params) {
    return { evid: 36, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketManageAppsUpgradeAppClick' event (evid: 38)
 *
 * Event description: in manage apps - click on upgrade button for apps
 *
 * @param {appMarketManageAppsUpgradeAppClickParams} params - The parameters for this event
 * @property {string} pp_origin - pp_origin
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} app_site_id - app_site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketManageAppsUpgradeAppClickParams>} LogObject<appMarketManageAppsUpgradeAppClickParams>
 */
export function appMarketManageAppsUpgradeAppClick(params) {
    return { evid: 38, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSiteCategorySelect' event (evid: 40)
 *
 * Event description: click on View Apps button in Site Category pop up
 *
 * @param {appMarketSiteCategorySelectParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} origin_category - origin_category
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {string} category - category
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketSiteCategorySelectParams>} LogObject<appMarketSiteCategorySelectParams>
 */
export function appMarketSiteCategorySelect(params) {
    return { evid: 40, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketConsentPageAddAppClick' event (evid: 43)
 *
 * Event description: permissions modal -> add app click
 *
 * @param {appMarketConsentPageAddAppClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} message_type - message_type
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketConsentPageAddAppClickParams>} LogObject<appMarketConsentPageAddAppClickParams>
 */
export function appMarketConsentPageAddAppClick(params) {
    return { evid: 43, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketOverrideSiteCategory' event (evid: 45)
 *
 * Event description: override site profile, when choosing a site profile in the appmarket (e.g online-store, photography , music etc) - this will affect the default apps presented in the home page and collection order.
 *
 * @param {appMarketOverrideSiteCategoryParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} esi - esi
 * @property {string} cat - category
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketOverrideSiteCategoryParams>} LogObject<appMarketOverrideSiteCategoryParams>
 */
export function appMarketOverrideSiteCategory(params) {
    return { evid: 45, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSsrLoaded' event (evid: 46)
 *
 * Event description: SSR loaded, ssr means - Server Side Loading. when loading the app market, first the "looks" is loaded, then the images, than the links.
 *
 * @param {appMarketSsrLoadedParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {number} ts - ts
 * @property {string} referral_info - referral_info
 * @property {string} referral_info_category - referral_info_category
 * @property {string} marketVersion - market_version
 * @property {boolean} is_first - is_first
 * @property {string} vertical - vertical
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketSsrLoadedParams>} LogObject<appMarketSsrLoadedParams>
 */
export function appMarketSsrLoaded(params) {
    return { evid: 46, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketWishlistWishlistClick' event (evid: 51)
 *
 * Event description: wishlist -> wishlist click
 *
 * @param {appMarketWishlistWishlistClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} search_term - search_term
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} num_results - num_results
 * @return {LogObject<appMarketWishlistWishlistClickParams>} LogObject<appMarketWishlistWishlistClickParams>
 */
export function appMarketWishlistWishlistClick(params) {
    return { evid: 51, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketWishlistWishlistLoaded' event (evid: 52)
 *
 * Event description: wishlist -> wishlist loaded
 *
 * @param {appMarketWishlistWishlistLoadedParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} search_term - search_term
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} num_results - num_results
 * @return {LogObject<appMarketWishlistWishlistLoadedParams>} LogObject<appMarketWishlistWishlistLoadedParams>
 */
export function appMarketWishlistWishlistLoaded(params) {
    return { evid: 52, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketWishlistWishlistClosed' event (evid: 53)
 *
 * Event description: wishlist -> wishlist closed
 *
 * @param {appMarketWishlistWishlistClosedParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} closing_method - closing_method
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketWishlistWishlistClosedParams>} LogObject<appMarketWishlistWishlistClosedParams>
 */
export function appMarketWishlistWishlistClosed(params) {
    return { evid: 53, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketWishlistSendClick' event (evid: 54)
 *
 * Event description: wishlist -> send click
 *
 * @param {appMarketWishlistSendClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} search_term - search_term
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} num_results - num_results
 * @return {LogObject<appMarketWishlistSendClickParams>} LogObject<appMarketWishlistSendClickParams>
 */
export function appMarketWishlistSendClick(params) {
    return { evid: 54, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketWishlistSent' event (evid: 55)
 *
 * Event description: wishlist -> sent
 *
 * @param {appMarketWishlistSentParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {string} description - description
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} search_term - search_term
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {string} title - title
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} num_results - num_results
 * @return {LogObject<appMarketWishlistSentParams>} LogObject<appMarketWishlistSentParams>
 */
export function appMarketWishlistSent(params) {
    return { evid: 55, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketWishlistSuccessMessageClosed' event (evid: 56)
 *
 * Event description: wishlist -> success message -> close
 *
 * @param {appMarketWishlistSuccessMessageClosedParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} closing_method - closing_method
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketWishlistSuccessMessageClosedParams>} LogObject<appMarketWishlistSuccessMessageClosedParams>
 */
export function appMarketWishlistSuccessMessageClosed(params) {
    return { evid: 56, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketWishlistSuccessMessageAddAgainClick' event (evid: 57)
 *
 * Event description: wishlist -> success message -> add again click
 *
 * @param {appMarketWishlistSuccessMessageAddAgainClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} vertical - vertical
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketWishlistSuccessMessageAddAgainClickParams>} LogObject<appMarketWishlistSuccessMessageAddAgainClickParams>
 */
export function appMarketWishlistSuccessMessageAddAgainClick(params) {
    return { evid: 57, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketCollectionLoaded' event (evid: 58)
 *
 * Event description: loading app market collection with the list of apps.
 * When only 4 apps are available (missing the main vertical app) - we should populate values 2 through 5, leaving 1 empty. created by pavelk
 *
 * @param {appMarketCollectionLoadedParams} params - The parameters for this event
 * @property {GUID} app_id1 - app_id1
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {GUID} app_id5 - app_id5
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} app_id2 - app_id2
 * @property {string} category - category
 * @property {GUID} esi - esi
 * @property {GUID} app_id4 - app_id4
 * @property {GUID} msid - msid
 * @property {GUID} app_id3 - app_id3
 * @return {LogObject<appMarketCollectionLoadedParams>} LogObject<appMarketCollectionLoadedParams>
 */
export function appMarketCollectionLoaded(params) {
    return { evid: 58, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketPendingRemoveAppFromPendingClick' event (evid: 61)
 *
 * Event description: pending -> remove app from pending click
 *
 * @param {appMarketPendingRemoveAppFromPendingClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketPendingRemoveAppFromPendingClickParams>} LogObject<appMarketPendingRemoveAppFromPendingClickParams>
 */
export function appMarketPendingRemoveAppFromPendingClick(params) {
    return { evid: 61, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketPendingAppRemovedFromPending' event (evid: 62)
 *
 * Event description: pending -> app removed from pending
 *
 * @param {appMarketPendingAppRemovedFromPendingParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketPendingAppRemovedFromPendingParams>} LogObject<appMarketPendingAppRemovedFromPendingParams>
 */
export function appMarketPendingAppRemovedFromPending(params) {
    return { evid: 62, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketHeaderClick' event (evid: 64)
 *
 * Event description: When in App Market, clicking on Tabs (App Market, Manage Apps)
 *
 * @param {appMarketHeaderClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} tabName - tab_name
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketHeaderClickParams>} LogObject<appMarketHeaderClickParams>
 */
export function appMarketHeaderClick(params) {
    return { evid: 64, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketConnectDomainClick' event (evid: 74)
 *
 * Event description: Clicking on "Connect Domain"
 *
 * @param {appMarketConnectDomainClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketConnectDomainClickParams>} LogObject<appMarketConnectDomainClickParams>
 */
export function appMarketConnectDomainClick(params) {
    return { evid: 74, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketCloseDialogBoxOfConnectDomainX' event (evid: 75)
 *
 * Event description: When trying to install Apps that require premium wix site, prompt will be opened, This BI event is sent when box is closed.
 *
 * @param {appMarketCloseDialogBoxOfConnectDomainXParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketCloseDialogBoxOfConnectDomainXParams>} LogObject<appMarketCloseDialogBoxOfConnectDomainXParams>
 */
export function appMarketCloseDialogBoxOfConnectDomainX(params) {
    return { evid: 75, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketTpaPurchasePageContactSupportWhenFailed' event (evid: 80)
 *
 * Event description: Dev Center - when Checkout failed External Package Picker - user clicked on 'contact support'
 *
 * @param {appMarketTpaPurchasePageContactSupportWhenFailedParams} params - The parameters for this event
 * @property {string} vendorProductId - vendor_id
 * @property {string} vertical - vertical
 * @property {GUID} appId - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketTpaPurchasePageContactSupportWhenFailedParams>} LogObject<appMarketTpaPurchasePageContactSupportWhenFailedParams>
 */
export function appMarketTpaPurchasePageContactSupportWhenFailed(params) {
    return { evid: 80, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketUnificationUnificationModalLoaded' event (evid: 90)
 *
 * Event description: unification -> unification modal loaded
 *
 * @param {appMarketUnificationUnificationModalLoadedParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} value - value
 * @return {LogObject<appMarketUnificationUnificationModalLoadedParams>} LogObject<appMarketUnificationUnificationModalLoadedParams>
 */
export function appMarketUnificationUnificationModalLoaded(params) {
    return { evid: 90, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketUnificationCloseUnificationModalClick' event (evid: 92)
 *
 * Event description: unification -> close unification modal click
 *
 * @param {appMarketUnificationCloseUnificationModalClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {string} closing_method - closing_method
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} value - value
 * @return {LogObject<appMarketUnificationCloseUnificationModalClickParams>} LogObject<appMarketUnificationCloseUnificationModalClickParams>
 */
export function appMarketUnificationCloseUnificationModalClick(params) {
    return { evid: 92, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketUnificationGoToClick' event (evid: 93)
 *
 * Event description: unification -> 'go to' click
 *
 * @param {appMarketUnificationGoToClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} go_to_market - go_to_market
 * @property {GUID} site_id - site_id
 * @property {GUID} uuid - uuid
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} origin - origin
 * @property {string} pending_source - pending_source
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} value - value
 * @return {LogObject<appMarketUnificationGoToClickParams>} LogObject<appMarketUnificationGoToClickParams>
 */
export function appMarketUnificationGoToClick(params) {
    return { evid: 93, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketManageAppsReadMoreAction' event (evid: 99)
 *
 * Event description: user browse through the apps image gallery
 *
 * @param {appMarketManageAppsReadMoreActionParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} action - action
 * @return {LogObject<appMarketManageAppsReadMoreActionParams>} LogObject<appMarketManageAppsReadMoreActionParams>
 */
export function appMarketManageAppsReadMoreAction(params) {
    return { evid: 99, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketClickOnMenu' event (evid: 101)
 *
 * Event description: click on menu
 *
 * @param {appMarketClickOnMenuParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {string} page_id - page_id
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketClickOnMenuParams>} LogObject<appMarketClickOnMenuParams>
 */
export function appMarketClickOnMenu(params) {
    return { evid: 101, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketHoverOverAppsCategory' event (evid: 103)
 *
 * Event description: hover on category
 *
 * @param {appMarketHoverOverAppsCategoryParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} site_id - site_id
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} category - category
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketHoverOverAppsCategoryParams>} LogObject<appMarketHoverOverAppsCategoryParams>
 */
export function appMarketHoverOverAppsCategory(params) {
    return { evid: 103, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketPageScroll' event (evid: 119)
 *
 * Event description: How much did the user scrolled down while browsing different pages in the App Market.
 *
 * @param {appMarketPageScrollParams} params - The parameters for this event
 * @property {string} market - market
 * @property {number} pixels_scrolled - pixels_scrolled
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {string} pageType - page_type
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} tag_name - tag_name
 * @return {LogObject<appMarketPageScrollParams>} LogObject<appMarketPageScrollParams>
 */
export function appMarketPageScroll(params) {
    return { evid: 119, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketCollectionsPills' event (evid: 137)
 *
 * Event description: In the Home page ----> discovery flow, each pill is collection.
 *
 * @param {appMarketCollectionsPillsParams} params - The parameters for this event
 * @property {string} market - market
 * @property {GUID} collimp_id - collimp_id
 * @property {string} slug_id - slug_id
 * @property {string} vertical - vertical
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {number} index - index
 * @return {LogObject<appMarketCollectionsPillsParams>} LogObject<appMarketCollectionsPillsParams>
 */
export function appMarketCollectionsPills(params) {
    return { evid: 137, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketDeleteModalClickOnCustomerCare' event (evid: 141)
 *
 * Event description: User clicks on contact Customer Care button in the delete modal
 *
 * @param {appMarketDeleteModalClickOnCustomerCareParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} vertical - vertical
 * @property {GUID} app_id - app_id
 * @property {string} tag_type - tag_type
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketDeleteModalClickOnCustomerCareParams>} LogObject<appMarketDeleteModalClickOnCustomerCareParams>
 */
export function appMarketDeleteModalClickOnCustomerCare(params) {
    return { evid: 141, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSubCategoryHover' event (evid: 142)
 *
 * Event description: User hovers on a sub-category tag, to filter in (or out) the apps that are presented in a category page.
 *
 * @param {appMarketSubCategoryHoverParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} category_name - category_name
 * @property {string} subcategory_name - subcategory_name
 * @property {string} loaction - location
 * @property {string} vertical - vertical
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketSubCategoryHoverParams>} LogObject<appMarketSubCategoryHoverParams>
 */
export function appMarketSubCategoryHover(params) {
    return { evid: 142, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSaleCheckAllSaleAppsCollectionClick' event (evid: 143)
 *
 * Event description: App Market - Sale - Check All Sale Apps Click
 *
 * @param {appMarketSaleCheckAllSaleAppsCollectionClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} slug_id - slug_id
 * @property {string} vertical - vertical
 * @property {string} sale_name - sale_name
 * @property {string} tag_type - tag_type
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} button_location - button_location
 * @return {LogObject<appMarketSaleCheckAllSaleAppsCollectionClickParams>} LogObject<appMarketSaleCheckAllSaleAppsCollectionClickParams>
 */
export function appMarketSaleCheckAllSaleAppsCollectionClick(params) {
    return { evid: 143, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSaleUpgradePlanClick' event (evid: 144)
 *
 * Event description: App Market -  Sale - Upgrade Plan Click
 *
 * @param {appMarketSaleUpgradePlanClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} slug_id - slug_id
 * @property {string} vertical - vertical
 * @property {string} sale_name - sale_name
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} referral_tag_type - referral_tag_type
 * @property {string} button_location - button_location
 * @return {LogObject<appMarketSaleUpgradePlanClickParams>} LogObject<appMarketSaleUpgradePlanClickParams>
 */
export function appMarketSaleUpgradePlanClick(params) {
    return { evid: 144, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSaleCheckAllSaleAppsHeaderClick' event (evid: 146)
 *
 * Event description: App Market - Sale - Check All Sale Apps Click
 *
 * @param {appMarketSaleCheckAllSaleAppsHeaderClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} slug_id - slug_id
 * @property {string} vertical - vertical
 * @property {string} sale_name - sale_name
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {string} referral_tag_type - referral_tag_type
 * @return {LogObject<appMarketSaleCheckAllSaleAppsHeaderClickParams>} LogObject<appMarketSaleCheckAllSaleAppsHeaderClickParams>
 */
export function appMarketSaleCheckAllSaleAppsHeaderClick(params) {
    return { evid: 146, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketAppUpgradeUpgradeButtonClick' event (evid: 149)
 *
 * Event description: AppMarket - App Upgrade - Upgrade Button Click.
 *
 * @param {appMarketAppUpgradeUpgradeButtonClickParams} params - The parameters for this event
 * @property {string} pp_origin - pp_origin
 * @property {string} billing_flow - billing_flow
 * @property {string} vendorProductId - vendor_id
 * @property {string} coupon_name - coupon_name
 * @property {string} vertical - vertical
 * @property {string} sale_name - sale_name
 * @property {GUID} appId - app_id
 * @property {GUID} esi - esi
 * @property {number} payment_cycle - cycle_id
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketAppUpgradeUpgradeButtonClickParams>} LogObject<appMarketAppUpgradeUpgradeButtonClickParams>
 */
export function appMarketAppUpgradeUpgradeButtonClick(params) {
    return { evid: 149, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketDashboardWidgetExploreMoreAppsClick' event (evid: 165)
 *
 * Event description: User clicks on explore more apps in the business manager widget panel.
 *
 * @param {appMarketDashboardWidgetExploreMoreAppsClickParams} params - The parameters for this event
 * @property {string} marketVersion - market_version
 * @property {string} vertical - vertical
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketDashboardWidgetExploreMoreAppsClickParams>} LogObject<appMarketDashboardWidgetExploreMoreAppsClickParams>
 */
export function appMarketDashboardWidgetExploreMoreAppsClick(params) {
    return { evid: 165, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketSortingAppsClick' event (evid: 176)
 *
 * Event description: Triggers when user clicks on the sorting button of the page
 *
 * @param {appMarketSortingAppsClickParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} sort_type - sort_type
 * @property {string} vertical - vertical
 * @property {string} tag_type - tag_type
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketSortingAppsClickParams>} LogObject<appMarketSortingAppsClickParams>
 */
export function appMarketSortingAppsClick(params) {
    return { evid: 176, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketDowngradeCalculateClick' event (evid: 407)
 *
 * Event description: downgrade modal opens
 *
 * @param {appMarketDowngradeCalculateClickParams} params - The parameters for this event
 * @property {string} pp_origin - pp_origin
 * @property {string} vertical - vertical
 * @property {GUID} app_id - app_id
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @property {GUID} instance_id - instance_id
 * @return {LogObject<appMarketDowngradeCalculateClickParams>} LogObject<appMarketDowngradeCalculateClickParams>
 */
export function appMarketDowngradeCalculateClick(params) {
    return { evid: 407, src: 24, endpoint: '', params: params };
}
/**
 * A function to create a log object for the 'appMarketAutocompleteFailed' event (evid: 1103)
 *
 * Event description: autocomplete Failed
 *
 * @param {appMarketAutocompleteFailedParams} params - The parameters for this event
 * @property {string} market - market
 * @property {string} vertical - vertical
 * @property {string} autocomplete_query - autocomplete_query
 * @property {string} category - category
 * @property {GUID} app_id - app_id
 * @property {string} type - type
 * @property {GUID} esi - esi
 * @property {GUID} msid - msid
 * @return {LogObject<appMarketAutocompleteFailedParams>} LogObject<appMarketAutocompleteFailedParams>
 */
export function appMarketAutocompleteFailed(params) {
    return { evid: 1103, src: 24, endpoint: '', params: params };
}
//# sourceMappingURL=index.js.map